// TMOS Template Builder
// Date: January 26, 2025
// Purpose: Visual interface for creating hierarchical roadmap templates

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../../state/language/LanguageProvider';
import { useAuth } from '../../state/auth/AuthProvider';
import { PureHierarchicalTemplate, PureVision, PureObjective, PureStrategy, PureProject, PureInchstone, PureKPI, PureRisk, PureStakeholder } from '../../types/pureHierarchicalTemplate_v2';
import templateService from '../../services/templateService';
import { VisualRoadmapTimeline } from '../../components/roadmap/VisualRoadmapTimeline';
import { RoadmapProject } from '../../services/roadmapMarketplace';
import { extractProjectsFromHierarchy } from '../../utils/roadmapProjectExtractor';
import api from '../../services/api';
import { PageContextWrapper, getContextButtonClasses } from '../../components/roadmap/PageContextWrapper';
import { AlignmentTypeahead } from '../../components/alignment/AlignmentTypeahead';
import {
  buildObjectiveName,
  DEFAULT_OBJECTIVE_VALUE_TYPE,
  parseObjectiveKpiInput,
  type ObjectiveValueType
} from '../../components/alignment/objectiveName';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';

// =====================================================
// TEMPLATE BUILDER
// =====================================================

export const PureHierarchicalTemplateBuilder: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user } = useAuth();

  // Check if we're in edit mode (template or roadmap)
  const editTemplateId = searchParams.get('edit');
  const editRoadmapId = searchParams.get('roadmap');
  const isEditMode = !!editTemplateId || !!editRoadmapId;
  // Roadmap mode: if roadmap ID in URL OR if we're on the roadmap builder route
  const isRoadmapMode = !!editRoadmapId || location.pathname.includes('/tenant/roadmaps/builder');
  
  // Determine page context for visual differentiation
  const pageContext = isRoadmapMode ? 'tenant-roadmap-builder' : 'template-builder';

  // Debug user authentication
  useEffect(() => {
              }, [user]);

  // Store the original template to preserve marketplace_visibility
  const [originalTemplate, setOriginalTemplate] = useState<any>(null);
  // Store the original roadmap ID for roadmap mode
  const [originalRoadmapId, setOriginalRoadmapId] = useState<string | null>(null);

  // Load existing template or roadmap when in edit mode
  useEffect(() => {
    if (isRoadmapMode && editRoadmapId) {
      loadRoadmapForEditing(editRoadmapId);
    } else if (isEditMode && editTemplateId) {
      loadExistingTemplate(editTemplateId);
    }
    
    // Cleanup: Clear autosave timeout on unmount
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [isRoadmapMode, editRoadmapId, isEditMode, editTemplateId]);

  useEffect(() => {
    const loadPlatformKpis = async () => {
      try {
        const response = await api.get('/kpis', { params: { platformCatalog: true } });
        if (response.data.success) {
          setPlatformKpis(response.data.data || []);
        }
      } catch (err) {
        console.error('Failed to load platform KPI catalog', err);
      }
    };
    loadPlatformKpis();
  }, []);

  const loadExistingTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      const existingTemplate = await templateService.getTemplate(templateId);
      
      // Store the original template to preserve marketplace_visibility
      setOriginalTemplate(existingTemplate);
      
      // Check if the template content is a PureHierarchicalTemplate
      if (existingTemplate.content && existingTemplate.content.template_metadata) {
        // Ensure template_metadata has all required fields with defaults
        const loadedContent = {
          ...existingTemplate.content,
          template_metadata: {
            name: existingTemplate.content.template_metadata.name || existingTemplate.name || t('templates.untitledTemplate'),
            description: existingTemplate.content.template_metadata.description || existingTemplate.description || '',
            industry: existingTemplate.content.template_metadata.industry || 'General',
            functional_area: existingTemplate.content.template_metadata.functional_area || 'General',
            complexity_level: existingTemplate.content.template_metadata.complexity_level || 1,
            estimated_duration_months: existingTemplate.content.template_metadata.estimated_duration_months || 12,
            tags: existingTemplate.content.template_metadata.tags || [],
            version: existingTemplate.content.template_metadata.version || existingTemplate.version || '1.0',
            created_by: existingTemplate.content.template_metadata.created_by || existingTemplate.created_by || user?.id || '',
            created_at: existingTemplate.content.template_metadata.created_at || existingTemplate.created_at || new Date().toISOString(),
            // Optional fields for backward compatibility
            transformation_type: existingTemplate.content.template_metadata.transformation_type || undefined,
            estimated_cost_range_min: existingTemplate.content.template_metadata.estimated_cost_range_min || undefined,
            estimated_cost_range_max: existingTemplate.content.template_metadata.estimated_cost_range_max || undefined,
            status: existingTemplate.content.template_metadata.status || undefined,
            ...existingTemplate.content.template_metadata
          },
          hierarchy: existingTemplate.content.hierarchy || { visions: [] }
        };
        setTemplate(loadedContent);
      } else {
        setError(t('templates.errors.cannotEditWithHierarchicalBuilder'));
      }
    } catch (err) {
      console.error('Error loading template for editing:', err);
      setError(t('templates.errors.failedToLoadTemplateForEditing'));
    } finally {
      setLoading(false);
    }
  };

  // Load roadmap data and convert to template format for editing
  const loadRoadmapForEditing = async (roadmapId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get roadmap data (already includes hierarchical_content in template format)
      const response = await api.get(`/tenant/roadmaps/${roadmapId}/editor-data`);
      
      if (!response.data.success || !response.data.data) {
        throw new Error('Failed to load roadmap data');
      }
      
      const roadmapData = response.data.data;
      
      // Use hierarchical_content (already in template format)
      if (roadmapData.hierarchical_content && roadmapData.hierarchical_content.template_metadata) {
        const loadedContent = {
          ...roadmapData.hierarchical_content,
          template_metadata: {
            name: roadmapData.roadmap?.name || roadmapData.hierarchical_content.template_metadata.name || 'Untitled Roadmap',
            description: roadmapData.roadmap?.description || roadmapData.hierarchical_content.template_metadata.description || '',
            industry: roadmapData.roadmap?.industry || roadmapData.hierarchical_content.template_metadata.industry || 'General',
            functional_area: roadmapData.roadmap?.functional_area || roadmapData.hierarchical_content.template_metadata.functional_area || 'General',
            complexity_level: roadmapData.roadmap?.complexity_level || roadmapData.hierarchical_content.template_metadata.complexity_level || 1,
            estimated_duration_months: roadmapData.roadmap?.estimated_duration_months || roadmapData.hierarchical_content.template_metadata.estimated_duration_months || 12,
            tags: roadmapData.roadmap?.tags || roadmapData.hierarchical_content.template_metadata.tags || [],
            version: '1.0',
            created_by: roadmapData.roadmap?.created_by || user?.id || '',
            created_at: roadmapData.roadmap?.created_at || new Date().toISOString(),
            ...roadmapData.hierarchical_content.template_metadata
          },
          hierarchy: roadmapData.hierarchical_content.hierarchy || { visions: [] }
        };
        setTemplate(loadedContent);
        setOriginalRoadmapId(roadmapId);
      } else {
        setError('Roadmap data is not in the expected format');
      }
    } catch (err) {
      console.error('Error loading roadmap for editing:', err);
      setError(err instanceof Error ? err.message : 'Failed to load roadmap for editing');
    } finally {
      setLoading(false);
    }
  };

  // Template state
  const [template, setTemplate] = useState<PureHierarchicalTemplate>({
    template_metadata: {
      name: t('templates.newTemplate'),
      description: '',
      industry: 'General',
      functional_area: 'General',
      complexity_level: 1,
      estimated_duration_months: 12,
      tags: [],
      version: '1.0',
      created_by: user?.id || '',
      created_at: new Date().toISOString()
    },
    hierarchy: {
      visions: []
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmedDelete, setConfirmedDelete] = useState(false);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [jsonContent, setJsonContent] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [viewingFile, setViewingFile] = useState<{name: string, content: string, mimeType: string} | null>(null);
  // const [showVisualTimeline, setShowVisualTimeline] = useState(false); // Unused - kept for future use
  const [activeTab, setActiveTab] = useState<'metadata' | 'timeline' | 'json' | 'builder'>('metadata');
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [platformKpis, setPlatformKpis] = useState<Array<{ id: string; name: string; unit?: string; type?: string }>>([]);
  const [pendingUpdates, setPendingUpdates] = useState<Set<string>>(new Set());
  const draggedItemRef = useRef<{type: 'vision' | 'objective' | 'strategy' | 'project' | 'program', path: number[], data: any} | null>(null);
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Note: JSON Editor is now available for roadmaps (read-only when editing existing roadmap)

  // Confirmation function for delete operations
  const confirmDelete = (action: () => void, itemName: string) => {
    if (confirmedDelete) {
      action();
    } else {
      const confirmed = window.confirm(t('templates.confirmDelete').replace('{itemName}', itemName));
      if (confirmed) {
        setConfirmedDelete(true);
        action();
      }
    }
  };

  // Helper function to check if an item is existing (has id) - for roadmap mode read-only
  const isExistingItem = (item: any): boolean => {
    return !!(item?.id || item?.existing_id);
  };

  // Helper to check if item should be read-only (roadmap mode + existing item)
  const isReadOnly = (item: any): boolean => {
    return isRoadmapMode && isExistingItem(item);
  };

  // Collapse/expand helper functions
  const toggleCollapse = (sectionKey: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const isCollapsed = (sectionKey: string) => {
    return collapsedSections[sectionKey] || false;
  };

  // File viewer functions
  const openFileViewer = (file: any) => {
    setViewingFile({
      name: file.filename,
      content: file.content,
      mimeType: file.mime_type
    });
  };

  const closeFileViewer = () => {
    setViewingFile(null);
  };

  const downloadFile = (file: {name: string, content: string, mimeType: string}) => {
    try {
            
      // Remove data: prefix if it exists (in case content already has it)
      let content = file.content;
      if (content.startsWith('data:')) {
        content = content.split(',')[1];
      }
      
      const link = document.createElement('a');
      link.href = `data:${file.mimeType};base64,${content}`;
      link.download = file.name;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
          } catch (error) {
      console.error('Error downloading file:', error);
      alert(t('templates.errors.downloadFileError') || 'Error downloading file. Please try again.');
    }
  };

  // Industry options
  const industryOptions = [
    { value: 'General', label: t('templates.industries.general') || 'General' },
    { value: 'Cross-Industry', label: t('templates.industries.crossIndustry') || 'Cross-Industry' },
    { value: 'Manufacturing', label: t('templates.industries.manufacturing') || 'Manufacturing' },
    { value: 'Healthcare', label: t('templates.industries.healthcare') || 'Healthcare' },
    { value: 'Financial Services', label: t('templates.industries.financialServices') || 'Financial Services' },
    { value: 'Retail', label: t('templates.industries.retail') || 'Retail' },
    { value: 'Technology', label: t('templates.industries.technology') || 'Technology' },
    { value: 'Energy', label: t('templates.industries.energy') || 'Energy' },
    { value: 'Transportation', label: t('templates.industries.transportation') || 'Transportation' },
    { value: 'Education', label: t('templates.industries.education') || 'Education' },
    { value: 'Government', label: t('templates.industries.government') || 'Government' }
  ];

  // =====================================================
  // VISION MANAGEMENT
  // =====================================================

  const addVision = () => {
    const newVision: PureVision = {
      title: t('templates.newVision'),
      description: t('templates.visionDescription'),
      priority: 'HIGH',
      target_date: '',
      success_criteria: [],
      risks: [],
      stakeholders: [],
      objectives: []
    };

    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: [...prev.hierarchy.visions, newVision]
      }
    }));
  };

  const updateVision = (index: number, updatedVision: PureVision) => {
    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === index ? updatedVision : vision
        )
      }
    }));
  };

  const removeVision = (index: number) => {
    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.filter((_, i) => i !== index)
      }
    }));
  };

  // =====================================================
  // DRAG AND DROP HANDLERS
  // =====================================================

  const handleDragStart = (e: React.DragEvent, type: 'vision' | 'objective' | 'strategy' | 'project' | 'program', path: number[], data: any) => {
    draggedItemRef.current = { type, path, data };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ type, path })); // Store in dataTransfer for reliability
  };

  // Unused function - drag over is handled by drop zones
  // @ts-expect-error - Suppress unused variable warning
  const _handleDragOver = (_e: React.DragEvent) => {
    // Unused - drag over is handled by drop zones
  };

  const handleDragEnd = (_e: React.DragEvent) => {
    // Only clear if drop didn't happen (drop handler will clear it if drop succeeded)
    // We can't reliably detect if drop happened here, so we'll let the drop handler clear it
    // If no drop happened, the ref will remain but that's okay for next drag
  };

  // Helper function to check if a project is a descendant of another project by traversing projects
  // This is a simplified check - we'll use title matching since we don't have IDs in the path-based structure
  const isDescendantOf = (potentialDescendantPath: number[], ancestorPath: number[], template: PureHierarchicalTemplate): boolean => {
    // A project cannot be a descendant of itself
    if (potentialDescendantPath.length === ancestorPath.length && 
        potentialDescendantPath.every((val, idx) => val === ancestorPath[idx])) {
      return false;
    }
    
    // Get the ancestor project
    if (ancestorPath.length !== 4) return false; // Only projects have path length 4
    
    const ancestorStrategy = template.hierarchy.visions[ancestorPath[0]]?.objectives[ancestorPath[1]]?.strategies[ancestorPath[2]];
    if (!ancestorStrategy?.projects || !ancestorStrategy.projects[ancestorPath[3]]) {
      return false;
    }
    
    const ancestorProject = ancestorStrategy.projects[ancestorPath[3]];
    const descendantStrategy = template.hierarchy.visions[potentialDescendantPath[0]]?.objectives[potentialDescendantPath[1]]?.strategies[potentialDescendantPath[2]];
    const descendantProject = descendantStrategy?.projects?.[potentialDescendantPath[3]];
    
    if (!descendantProject) return false;
    
    // Recursively check if the potential descendant is nested under the ancestor
    const checkInSubProjects = (project: any, targetTitle: string): boolean => {
      if (!project.projects || !Array.isArray(project.projects)) {
        return false;
      }
      
      // Check each sub-project
      for (let i = 0; i < project.projects.length; i++) {
        const subProject = project.projects[i];
        // Match by title (simplified - in production use IDs)
        if (subProject.title === targetTitle) {
          return true;
        }
        // Recursively check nested sub-projects
        if (checkInSubProjects(subProject, targetTitle)) {
          return true;
        }
      }
      
      return false;
    };
    
    // Check if the potential descendant is nested under the ancestor
    return checkInSubProjects(ancestorProject, descendantProject.title || '');
  };

  const handleDrop = (e: React.DragEvent, targetPath: number[], targetType: 'vision' | 'objective' | 'strategy' | 'project' | 'program') => {
    e.preventDefault();
    e.stopPropagation();
    
    const draggedItem = draggedItemRef.current;
    if (!draggedItem) {
      return;
    }

    // Don't allow dropping on itself
    if (draggedItem.path.length === targetPath.length && 
        draggedItem.path.every((val, idx) => val === targetPath[idx])) {
      draggedItemRef.current = null;
      return;
    }

    const newTemplate = JSON.parse(JSON.stringify(template)); // Deep clone
    
    // Case 1: Dropping project on a strategy (move to that strategy)
    if (draggedItem.type === 'project' && targetType === 'strategy' && targetPath.length === 3) {
      // Remove from original location (pass itemData to ensure correct removal)
      removeItemFromPath(newTemplate, draggedItem.path, draggedItem.data);
      
      // Add to target strategy's projects array
      const strategy = newTemplate.hierarchy.visions[targetPath[0]].objectives[targetPath[1]].strategies[targetPath[2]];
      if (!strategy.projects) {
        strategy.projects = [];
      }
      // Clear any projects reference when moving to top level
      const projectToAdd = { ...draggedItem.data };
      delete projectToAdd.projects; // Remove nested structure when moving to top level
      strategy.projects.push(projectToAdd);
      
      setTemplate(newTemplate);
      draggedItemRef.current = null;
      
      // Trigger autosave after hierarchy drag and drop
      triggerAutosave();
      return;
    }
    
    // Case 2: Dropping project on another project (nest it OR reorder)
    if (draggedItem.type === 'project' && targetType === 'project' && targetPath.length === 4) {
      // Check if target is a descendant of dragged project (prevent circular nesting)
      if (isDescendantOf(targetPath, draggedItem.path, newTemplate)) {
        draggedItemRef.current = null;
        return;
      }
      
      // Check if they're in the same strategy (reordering)
      if (draggedItem.path[0] === targetPath[0] && 
          draggedItem.path[1] === targetPath[1] && 
          draggedItem.path[2] === targetPath[2] &&
          draggedItem.path[3] !== targetPath[3]) {
        // Remove item from original location (pass itemData to ensure correct removal)
        removeItemFromPath(newTemplate, draggedItem.path, draggedItem.data);
        
        // Adjust target path if needed
        const adjustedTargetPath = [...targetPath];
        if (draggedItem.path[3] < targetPath[3]) {
          adjustedTargetPath[3] = targetPath[3] - 1;
        }
        
        // Add item to new location
        addItemToPath(newTemplate, adjustedTargetPath, draggedItem.data, 'project');
        
      setTemplate(newTemplate);
      draggedItemRef.current = null;
      
      // Trigger autosave after hierarchy drag and drop
      triggerAutosave();
      return;
      } else {
        // Different strategy or same project - nest it under target project
        // Remove from original location (pass itemData to ensure correct removal)
        removeItemFromPath(newTemplate, draggedItem.path, draggedItem.data);
        
        // Find the target project and add as sub-project
        const strategy = newTemplate.hierarchy.visions[targetPath[0]].objectives[targetPath[1]].strategies[targetPath[2]];
        if (strategy.projects && strategy.projects[targetPath[3]]) {
          const targetProject = strategy.projects[targetPath[3]];
          if (!targetProject.projects) {
            targetProject.projects = [];
          }
          // Clear any existing projects when nesting (to avoid nested nesting issues)
          const projectToNest = { ...draggedItem.data };
          if (projectToNest.projects) {
            // Keep projects if they exist (preserve nested structure)
          }
          targetProject.projects.push(projectToNest);
        }
        
      setTemplate(newTemplate);
      draggedItemRef.current = null;
      
      // Trigger autosave after hierarchy drag and drop
      triggerAutosave();
      return;
      }
    }
    
    // Case 3: Dropping project on a program (nest it)
    if (draggedItem.type === 'project' && targetType === 'program' && targetPath.length === 4) {
      // Remove from original location (pass itemData to ensure correct removal)
      removeItemFromPath(newTemplate, draggedItem.path, draggedItem.data);
      
      // Find the target program and add to its projects
      const strategy = newTemplate.hierarchy.visions[targetPath[0]].objectives[targetPath[1]].strategies[targetPath[2]];
      if (strategy.projects && strategy.projects[targetPath[3]]) {
        const targetProgram = strategy.projects[targetPath[3]];
        if (!targetProgram.projects) {
          targetProgram.projects = [];
        }
        const projectToAdd = { ...draggedItem.data };
        delete projectToAdd.projects; // Clear nested structure when moving to program
        targetProgram.projects.push(projectToAdd);
      }
      
      setTemplate(newTemplate);
      draggedItemRef.current = null;
      
      // Trigger autosave after hierarchy drag and drop
      triggerAutosave();
      return;
    }
    
    // Case 4: Reordering strategies (only within same objective)
    if (draggedItem.type === 'strategy' && targetType === 'strategy') {
      // Check they're in the same objective
      if (draggedItem.path[0] === targetPath[0] && draggedItem.path[1] === targetPath[1]) {
        // Remove item from original location
        removeItemFromPath(newTemplate, draggedItem.path);
        
        // Adjust target path if needed
        const adjustedTargetPath = [...targetPath];
        if (draggedItem.path[2] < targetPath[2]) {
          adjustedTargetPath[2] = targetPath[2] - 1;
        }
        
        // Add item to new location
        addItemToPath(newTemplate, adjustedTargetPath, draggedItem.data, 'strategy');
        
      setTemplate(newTemplate);
      draggedItemRef.current = null;
      
      // Trigger autosave after hierarchy drag and drop
      triggerAutosave();
      return;
      } else {
        void 0;
      }
    }

    // Case 5: Reordering other types (visions, objectives) within same level
    if (draggedItem.type === targetType && 
        (draggedItem.type === 'vision' || draggedItem.type === 'objective')) {
      // Remove item from original location
      removeItemFromPath(newTemplate, draggedItem.path);
      
      // Adjust target path if needed
      const adjustedTargetPath = [...targetPath];
      const sameLevel = draggedItem.path.length === targetPath.length;
      if (sameLevel) {
        const lastIndex = draggedItem.path.length - 1;
        if (draggedItem.path[lastIndex] < targetPath[lastIndex]) {
          adjustedTargetPath[lastIndex] = targetPath[lastIndex] - 1;
        }
      }
      
      // Add item to new location
      addItemToPath(newTemplate, adjustedTargetPath, draggedItem.data, targetType);
      
      setTemplate(newTemplate);
      draggedItemRef.current = null;
      
      // Trigger autosave after hierarchy drag and drop
      triggerAutosave();
      return;
    }
    
    // Otherwise, don't allow the drop
    draggedItemRef.current = null;
  };

  const removeItemFromPath = (template: PureHierarchicalTemplate, path: number[], itemData?: any) => {
    if (path.length === 1) {
      // Remove vision
      template.hierarchy.visions.splice(path[0], 1);
    } else if (path.length === 2) {
      // Remove objective
      template.hierarchy.visions[path[0]].objectives.splice(path[1], 1);
    } else if (path.length === 3) {
      // Remove strategy
      template.hierarchy.visions[path[0]].objectives[path[1]].strategies.splice(path[2], 1);
    } else if (path.length === 4) {
      // Remove project - need to check if it's nested in projects
      const strategy = template.hierarchy.visions[path[0]].objectives[path[1]].strategies[path[2]];
      
      // First, try to find and remove from top-level projects
      if (strategy.projects && path[3] < strategy.projects.length) {
        const projectToRemove = strategy.projects[path[3]];
        
        // If we have itemData, match by title to ensure we're removing the right one
        if (!itemData || projectToRemove.title === itemData.title) {
          strategy.projects.splice(path[3], 1);
          return;
        }
      }
      
      // If not found in top-level, search in projects recursively
      if (strategy.projects) {
        for (let i = 0; i < strategy.projects.length; i++) {
          const project = strategy.projects[i];
          if (project.projects && Array.isArray(project.projects)) {
            // Search in projects
            const index = project.projects.findIndex((subProj: any) => 
              itemData ? subProj.title === itemData.title : true
            );
            if (index !== -1) {
              project.projects.splice(index, 1);
              return;
            }
            
            // Recursively search deeper
            const found = removeFromSubProjects(project.projects, itemData);
            if (found) return;
          }
        }
      }
      
      // Check programs
      if (strategy.projects && path[3] < strategy.projects.length) {
        strategy.projects.splice(path[3], 1);
      }
    }
  };

  // Helper to recursively remove from projects
  const removeFromSubProjects = (subProjects: any[], itemData?: any): boolean => {
    for (let i = 0; i < subProjects.length; i++) {
      const subProj = subProjects[i];
      
      // Check if this is the item we want to remove
      if (itemData && subProj.title === itemData.title) {
        subProjects.splice(i, 1);
        return true;
      }
      
      // Recursively search in nested projects
      if (subProj.projects && Array.isArray(subProj.projects)) {
        const found = removeFromSubProjects(subProj.projects, itemData);
        if (found) return true;
      }
    }
    return false;
  };

  const addItemToPath = (template: PureHierarchicalTemplate, path: number[], data: any, type: 'vision' | 'objective' | 'strategy' | 'project' | 'program') => {
    if (type === 'vision' && path.length === 1) {
      template.hierarchy.visions.splice(path[0], 0, data);
    } else if (type === 'objective' && path.length === 2) {
      template.hierarchy.visions[path[0]].objectives.splice(path[1], 0, data);
    } else if (type === 'strategy' && path.length === 3) {
      template.hierarchy.visions[path[0]].objectives[path[1]].strategies.splice(path[2], 0, data);
    } else if (type === 'project' && path.length === 4) {
      const strategy = template.hierarchy.visions[path[0]].objectives[path[1]].strategies[path[2]];
      if (!strategy.projects) {
        strategy.projects = [];
      }
      strategy.projects.splice(path[3], 0, data);
    } else if (type === 'program' && path.length === 4) {
      const strategy = template.hierarchy.visions[path[0]].objectives[path[1]].strategies[path[2]];
      if (!strategy.projects) {
        strategy.projects = [];
      }
      strategy.projects.splice(path[3], 0, data);
    }
  };

  // =====================================================
  // OBJECTIVE MANAGEMENT
  // =====================================================

  const addObjective = (visionIndex: number) => {
    const newObjective: PureObjective = {
      title: t('templates.newObjective'),
      description: t('templates.objectiveDescription'),
      priority: 'HIGH',
      target_date: '',
      success_criteria: [],
      risks: [],
      kpis: [],
      kpi: '',
      value_type: DEFAULT_OBJECTIVE_VALUE_TYPE,
      kpi_value: null,
      year: null,
      stakeholders: [],
      strategies: []
    };

    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? { ...vision, objectives: [...vision.objectives, newObjective] }
            : vision
        )
      }
    }));
  };

  const applyObjectiveKpiFields = (
    objective: PureObjective,
    patch: Partial<PureObjective>
  ): PureObjective => {
    const next: PureObjective = { ...objective, ...patch };
    const catalog = platformKpis.find((kpi) => kpi.name === next.kpi);
    const generated = buildObjectiveName({
      valueType: next.value_type,
      kpiValue: next.kpi_value,
      kpiName: next.kpi,
      kpiUnit: catalog?.unit,
      kpiType: catalog?.type,
      year: next.year
    });
    if (generated) {
      next.title = generated;
    }
    return next;
  };

  const updateObjective = (visionIndex: number, objectiveIndex: number, updatedObjective: PureObjective) => {
    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? {
                ...vision,
                objectives: vision.objectives.map((objective, j) => 
                  j === objectiveIndex ? updatedObjective : objective
                )
              }
            : vision
        )
      }
    }));
  };

  const removeObjective = (visionIndex: number, objectiveIndex: number) => {
    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? {
                ...vision,
                objectives: vision.objectives.filter((_, j) => j !== objectiveIndex)
              }
            : vision
        )
      }
    }));
  };

  // =====================================================
  // STRATEGY MANAGEMENT
  // =====================================================

  const addStrategy = (visionIndex: number, objectiveIndex: number) => {
    const newStrategy: PureStrategy = {
      title: t('templates.newStrategy'),
      description: t('templates.strategyDescription'),
      priority: 'HIGH',
      target_date: '',
      success_criteria: [],
      risks: [],
      kpis: [],
      stakeholders: [],
      projects: []
    };

    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? {
                ...vision,
                objectives: vision.objectives.map((objective, j) => 
                  j === objectiveIndex 
                    ? { ...objective, strategies: [...objective.strategies, newStrategy] }
                    : objective
                )
              }
            : vision
        )
      }
    }));
  };

  const updateStrategy = (visionIndex: number, objectiveIndex: number, strategyIndex: number, updatedStrategy: PureStrategy) => {
    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? {
                ...vision,
                objectives: vision.objectives.map((objective, j) => 
                  j === objectiveIndex 
                    ? {
                        ...objective,
                        strategies: objective.strategies.map((strategy, k) => 
                          k === strategyIndex ? updatedStrategy : strategy
                        )
                      }
                    : objective
                )
              }
            : vision
        )
      }
    }));
  };

  const removeStrategy = (visionIndex: number, objectiveIndex: number, strategyIndex: number) => {
    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? {
                ...vision,
                objectives: vision.objectives.map((objective, j) => 
                  j === objectiveIndex 
                    ? {
                        ...objective,
                        strategies: objective.strategies.filter((_, k) => k !== strategyIndex)
                      }
                    : objective
                )
              }
            : vision
        )
      }
    }));
  };

  // =====================================================
  // PROJECT MANAGEMENT
  // =====================================================

  const addProject = (visionIndex: number, objectiveIndex: number, strategyIndex: number) => {
    const newProject: PureProject = {
      title: t('templates.newProject'),
      description: t('templates.projectDescription'),
      priority: 'HIGH',
      target_date: '',
      estimated_duration_months: 6, // Default 6 months
      estimated_cost: 100, // Default $100K
      success_criteria: [],
      risks: [],
      kpis: [],
      stakeholders: [],
      projects: [],
      inchstones: []
    };

    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? {
                ...vision,
                objectives: vision.objectives.map((objective, j) => 
                  j === objectiveIndex 
                    ? {
                        ...objective,
                        strategies: objective.strategies.map((strategy, k) => 
                          k === strategyIndex 
                            ? { ...strategy, projects: [...strategy.projects, newProject] }
                            : strategy
                        )
                      }
                    : objective
                )
              }
            : vision
        )
      }
    }));
  };

  // Helper to update nested project in template hierarchy
  const updateNestedProjectInTemplate = (
    prev: PureHierarchicalTemplate,
    visionIndex: number,
    objectiveIndex: number,
    strategyIndex: number,
    projectIndex: number,
    nestedPath: number[],
    updatedProject: PureProject
  ): PureHierarchicalTemplate => {
    const updateNested = (p: PureProject, path: number[], depth: number): PureProject => {
      if (depth === path.length) {
        return { ...p, ...updatedProject };
      }
      return {
        ...p,
        projects: p.projects?.map((np, idx) =>
          idx === path[depth] ? updateNested(np, path, depth + 1) : np
        ) || []
      };
    };

    return {
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? {
                ...vision,
                objectives: vision.objectives.map((objective, j) => 
                  j === objectiveIndex 
                    ? {
                        ...objective,
                        strategies: objective.strategies.map((strategy, k) => 
                          k === strategyIndex 
                            ? {
                                ...strategy,
                                projects: strategy.projects.map((project, l) => {
                                  if (l === projectIndex) {
                                    return nestedPath.length > 0 ? updateNested(project, nestedPath, 0) : updatedProject;
                                  }
                                  return project;
                                })
                              }
                            : strategy
                        )
                      }
                    : objective
                )
              }
            : vision
        )
      }
    };
  };

  const updateProject = (visionIndex: number, objectiveIndex: number, strategyIndex: number, projectIndex: number, updatedProject: PureProject, nestedPath?: number[]) => {
    setTemplate(prev => {
      if (nestedPath && nestedPath.length > 0) {
        return updateNestedProjectInTemplate(prev, visionIndex, objectiveIndex, strategyIndex, projectIndex, nestedPath, updatedProject);
      }
      // Original logic for top-level projects
      return {
        ...prev,
        hierarchy: {
          ...prev.hierarchy,
          visions: prev.hierarchy.visions.map((vision, i) => 
            i === visionIndex 
              ? {
                  ...vision,
                  objectives: vision.objectives.map((objective, j) => 
                    j === objectiveIndex 
                      ? {
                          ...objective,
                          strategies: objective.strategies.map((strategy, k) => 
                            k === strategyIndex 
                              ? {
                                  ...strategy,
                                  projects: strategy.projects.map((project, l) => 
                                    l === projectIndex ? updatedProject : project
                                  )
                                }
                              : strategy
                          )
                        }
                      : objective
                  )
                }
              : vision
          )
        }
      };
    });
  };

  // Helper to remove nested project from template hierarchy
  const removeNestedProjectFromTemplate = (
    prev: PureHierarchicalTemplate,
    visionIndex: number,
    objectiveIndex: number,
    strategyIndex: number,
    projectIndex: number,
    nestedPath: number[]
  ): PureHierarchicalTemplate => {
    const removeNested = (p: PureProject, path: number[], depth: number): PureProject => {
      if (depth === path.length - 1) {
        // Remove the project at this level
        return {
          ...p,
          projects: p.projects?.filter((_, idx) => idx !== path[depth]) || []
        };
      }
      return {
        ...p,
        projects: p.projects?.map((np, idx) =>
          idx === path[depth] ? removeNested(np, path, depth + 1) : np
        ) || []
      };
    };

    return {
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? {
                ...vision,
                objectives: vision.objectives.map((objective, j) => 
                  j === objectiveIndex 
                    ? {
                        ...objective,
                        strategies: objective.strategies.map((strategy, k) => 
                          k === strategyIndex 
                            ? {
                                ...strategy,
                                projects: strategy.projects.map((project, l) => {
                                  if (l === projectIndex) {
                                    return nestedPath.length > 0 ? removeNested(project, nestedPath, 0) : project;
                                  }
                                  return project;
                                })
                              }
                            : strategy
                        )
                      }
                    : objective
                )
              }
            : vision
        )
      }
    };
  };

  const removeProject = (visionIndex: number, objectiveIndex: number, strategyIndex: number, projectIndex: number, nestedPath?: number[]) => {
    setTemplate(prev => {
      if (nestedPath && nestedPath.length > 0) {
        return removeNestedProjectFromTemplate(prev, visionIndex, objectiveIndex, strategyIndex, projectIndex, nestedPath);
      }
      // Original logic for top-level projects
      return {
        ...prev,
        hierarchy: {
          ...prev.hierarchy,
          visions: prev.hierarchy.visions.map((vision, i) => 
            i === visionIndex 
              ? {
                  ...vision,
                  objectives: vision.objectives.map((objective, j) => 
                    j === objectiveIndex 
                      ? {
                          ...objective,
                          strategies: objective.strategies.map((strategy, k) => 
                            k === strategyIndex 
                              ? {
                                  ...strategy,
                                  projects: strategy.projects.filter((_, l) => l !== projectIndex)
                                }
                              : strategy
                          )
                        }
                      : objective
                  )
                }
              : vision
          )
        }
      };
    });
  };

  // Add project inside another project
  const addNestedProject = (visionIndex: number, objectiveIndex: number, strategyIndex: number, projectIndex: number, nestedPath: number[]) => {
    const newProject: PureProject = {
      title: t('templates.newProject') || 'New Project',
      description: '',
      priority: 'HIGH',
      target_date: '',
      estimated_duration_months: 6,
      estimated_cost: 100,
      success_criteria: [],
      risks: [],
      kpis: [],
      stakeholders: [],
      projects: [],
      inchstones: []
    };

    const addNested = (p: PureProject, path: number[], depth: number): PureProject => {
      if (depth === path.length) {
        return {
          ...p,
          projects: [...(p.projects || []), newProject]
        };
      }
      return {
        ...p,
        projects: p.projects?.map((np, idx) =>
          idx === path[depth] ? addNested(np, path, depth + 1) : np
        ) || []
      };
    };

    setTemplate(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        visions: prev.hierarchy.visions.map((vision, i) => 
          i === visionIndex 
            ? {
                ...vision,
                objectives: vision.objectives.map((objective, j) => 
                  j === objectiveIndex 
                    ? {
                        ...objective,
                        strategies: objective.strategies.map((strategy, k) => 
                          k === strategyIndex 
                            ? {
                                ...strategy,
                                projects: strategy.projects.map((project, l) => {
                                  if (l === projectIndex) {
                                    return nestedPath.length > 0 ? addNested(project, nestedPath, 0) : {
                                      ...project,
                                      projects: [...(project.projects || []), newProject]
                                    };
                                  }
                                  return project;
                                })
                              }
                            : strategy
                        )
                      }
                    : objective
                )
              }
            : vision
        )
      }
    }));
  };

  // =====================================================
  // KPI MANAGEMENT
  // =====================================================

  const addKPI = (path: number[], level: 'VISION' | 'OBJECTIVE' | 'STRATEGY' | 'PROJECT') => {
    const newKPI: PureKPI = {
      name: t('templates.newKPI'),
      description: t('templates.kpiDescription'),
      kpi_type: 'FINANCIAL',
      measurement_unit: 'units',
      target_value: 100,
      current_value: 0,
      frequency: 'MONTHLY',
      owner: 'TBD',
      is_critical: false
    };

    setTemplate(prev => {
      const newTemplate = JSON.parse(JSON.stringify(prev)); // Deep clone
      let current = newTemplate.hierarchy.visions[path[0]];
      
      if (level === 'VISION') {
        current.kpis = [...(current.kpis || []), newKPI];
      } else if (level === 'OBJECTIVE') {
        current = current.objectives[path[1]];
        current.kpis = [...(current.kpis || []), newKPI];
      } else if (level === 'STRATEGY') {
        current = current.objectives[path[1]].strategies[path[2]];
        current.kpis = [...(current.kpis || []), newKPI];
      } else if (level === 'PROJECT') {
        current = current.objectives[path[1]].strategies[path[2]].projects[path[3]];
        current.kpis = [...(current.kpis || []), newKPI];
      }
      
      return newTemplate;
    });
  };

  const removeKPI = (path: number[], kpiIndex: number, level: 'VISION' | 'OBJECTIVE' | 'STRATEGY' | 'PROJECT') => {
    setTemplate(prev => {
      const newTemplate = { ...prev };
      let current: any = newTemplate.hierarchy.visions[path[0]];
      
      if (level === 'VISION') {
        current.kpis = current.kpis?.filter((_: any, i: any) => i !== kpiIndex) || [];
      } else if (level === 'OBJECTIVE') {
        current = current.objectives[path[1]];
        current.kpis = current.kpis?.filter((_: any, i: any) => i !== kpiIndex) || [];
      } else if (level === 'STRATEGY') {
        current = current.objectives[path[1]].strategies[path[2]];
        current.kpis = current.kpis?.filter((_: any, i: any) => i !== kpiIndex) || [];
      } else if (level === 'PROJECT') {
        current = current.objectives[path[1]].strategies[path[2]].projects[path[3]];
        current.kpis = current.kpis?.filter((_: any, i: any) => i !== kpiIndex) || [];
      }
      
      return newTemplate;
    });
  };

  // =====================================================
  // RISK MANAGEMENT
  // =====================================================

  const addRisk = (path: number[], level: 'VISION' | 'OBJECTIVE' | 'STRATEGY' | 'PROJECT') => {
    const newRisk: PureRisk = {
      name: t('templates.newRisk'),
      description: t('templates.riskDescription'),
      risk_type: 'BUSINESS',
      probability: 'MEDIUM',
      impact: 'MEDIUM',
      mitigation_strategy: 'TBD',
      contingency_plan: 'TBD',
      owner: 'TBD',
      status: 'IDENTIFIED'
    };

    setTemplate(prev => {
      const newTemplate = JSON.parse(JSON.stringify(prev)); // Deep clone
      let current = newTemplate.hierarchy.visions[path[0]];
      
      if (level === 'VISION') {
        current.risks = [...(current.risks || []), newRisk];
      } else if (level === 'OBJECTIVE') {
        current = current.objectives[path[1]];
        current.risks = [...(current.risks || []), newRisk];
      } else if (level === 'STRATEGY') {
        current = current.objectives[path[1]].strategies[path[2]];
        current.risks = [...(current.risks || []), newRisk];
      } else if (level === 'PROJECT') {
        current = current.objectives[path[1]].strategies[path[2]].projects[path[3]];
        current.risks = [...(current.risks || []), newRisk];
      }
      
      return newTemplate;
    });
  };

  const removeRisk = (path: number[], riskIndex: number, level: 'VISION' | 'OBJECTIVE' | 'STRATEGY' | 'PROJECT') => {
    setTemplate(prev => {
      const newTemplate = { ...prev };
      let current: any = newTemplate.hierarchy.visions[path[0]];
      
      if (level === 'VISION') {
        current.risks = current.risks?.filter((_: any, i: any) => i !== riskIndex) || [];
      } else if (level === 'OBJECTIVE') {
        current = current.objectives[path[1]];
        current.risks = current.risks?.filter((_: any, i: any) => i !== riskIndex) || [];
      } else if (level === 'STRATEGY') {
        current = current.objectives[path[1]].strategies[path[2]];
        current.risks = current.risks?.filter((_: any, i: any) => i !== riskIndex) || [];
      } else if (level === 'PROJECT') {
        current = current.objectives[path[1]].strategies[path[2]].projects[path[3]];
        current.risks = current.risks?.filter((_: any, i: any) => i !== riskIndex) || [];
      }
      
      return newTemplate;
    });
  };

  // =====================================================
  // STAKEHOLDER MANAGEMENT
  // =====================================================

  const addStakeholder = (path: number[], level: 'VISION' | 'OBJECTIVE' | 'STRATEGY' | 'PROJECT') => {
    const newStakeholder: PureStakeholder = {
      name: t('templates.newStakeholder'),
      title: 'CxO',
      organization: 'Company',
      email: 'stakeholder@company.com',
      role: 'OWNER',
      is_external: false,
      subscription_preferences: {
        visions: true,
        objectives: true,
        strategies: true,
        projects: true,
        inchstones: true,
        risks: true,
        kpis: true,
        stakeholders: true
      }
    };

    setTemplate(prev => {
      const newTemplate = JSON.parse(JSON.stringify(prev)); // Deep clone
      let current = newTemplate.hierarchy.visions[path[0]];
      
      if (level === 'VISION') {
        current.stakeholders = [...(current.stakeholders || []), newStakeholder];
      } else if (level === 'OBJECTIVE') {
        current = current.objectives[path[1]];
        current.stakeholders = [...(current.stakeholders || []), newStakeholder];
      } else if (level === 'STRATEGY') {
        current = current.objectives[path[1]].strategies[path[2]];
        current.stakeholders = [...(current.stakeholders || []), newStakeholder];
      } else if (level === 'PROJECT') {
        current = current.objectives[path[1]].strategies[path[2]].projects[path[3]];
        current.stakeholders = [...(current.stakeholders || []), newStakeholder];
      }
      
      return newTemplate;
    });
  };

  const removeStakeholder = (path: number[], stakeholderIndex: number, level: 'VISION' | 'OBJECTIVE' | 'STRATEGY' | 'PROJECT') => {
    setTemplate(prev => {
      const newTemplate = { ...prev };
      let current: any = newTemplate.hierarchy.visions[path[0]];
      
      if (level === 'VISION') {
        current.stakeholders = current.stakeholders?.filter((_: any, i: any) => i !== stakeholderIndex) || [];
      } else if (level === 'OBJECTIVE') {
        current = current.objectives[path[1]];
        current.stakeholders = current.stakeholders?.filter((_: any, i: any) => i !== stakeholderIndex) || [];
      } else if (level === 'STRATEGY') {
        current = current.objectives[path[1]].strategies[path[2]];
        current.stakeholders = current.stakeholders?.filter((_: any, i: any) => i !== stakeholderIndex) || [];
      } else if (level === 'PROJECT') {
        current = current.objectives[path[1]].strategies[path[2]].projects[path[3]];
        current.stakeholders = current.stakeholders?.filter((_: any, i: any) => i !== stakeholderIndex) || [];
      }
      
      return newTemplate;
    });
  };

  // =====================================================
  // INCHSTONE MANAGEMENT
  // =====================================================

  const addInchstone = (visionIndex: number, objectiveIndex: number, strategyIndex: number, projectIndex: number, nestedPath?: number[]) => {
    // Navigate to the project using the path (supports nested projects)
    let project: PureProject;
    if (nestedPath && nestedPath.length > 0) {
      // Navigate through nested structure
      let currentProject = template.hierarchy.visions[visionIndex].objectives[objectiveIndex].strategies[strategyIndex].projects[projectIndex];
      for (let i = 0; i < nestedPath.length; i++) {
        if (!currentProject.projects) break;
        currentProject = currentProject.projects[nestedPath[i]];
      }
      project = currentProject;
    } else {
      // Top-level project
      project = template.hierarchy.visions[visionIndex].objectives[objectiveIndex].strategies[strategyIndex].projects[projectIndex];
    }

    const existingInchstones = project.inchstones || [];
    const nextWeekNumber = existingInchstones.length > 0 
      ? Math.max(...existingInchstones.map(i => i.week_number)) + 1 
      : 0;

    const newInchstone: PureInchstone = {
      title: t('templates.weekDeliverable').replace('{weekNumber}', nextWeekNumber.toString()),
      priority: 'HIGH',
      week_number: nextWeekNumber,
      estimated_duration_weeks: 2,
      success_criteria: [],
      inchstones: []
    };

    const addNestedInchstone = (p: PureProject, path: number[], depth: number): PureProject => {
      if (depth === path.length) {
        return { ...p, inchstones: [...(p.inchstones || []), newInchstone] };
      }
      return {
        ...p,
        projects: p.projects?.map((np, idx) =>
          idx === path[depth] ? addNestedInchstone(np, path, depth + 1) : np
        ) || []
      };
    };

    setTemplate(prev => {
      if (nestedPath && nestedPath.length > 0) {
        return {
          ...prev,
          hierarchy: {
            ...prev.hierarchy,
            visions: prev.hierarchy.visions.map((vision, i) => 
              i === visionIndex 
                ? {
                    ...vision,
                    objectives: vision.objectives.map((objective, j) => 
                      j === objectiveIndex 
                        ? {
                            ...objective,
                            strategies: objective.strategies.map((strategy, k) => 
                              k === strategyIndex 
                                ? {
                                    ...strategy,
                                    projects: strategy.projects.map((project, l) => {
                                      if (l === projectIndex) {
                                        return addNestedInchstone(project, nestedPath, 0);
                                      }
                                      return project;
                                    })
                                  }
                                : strategy
                            )
                          }
                        : objective
                    )
                  }
                : vision
            )
          }
        };
      }
      // Original logic for top-level projects
      return {
        ...prev,
        hierarchy: {
          ...prev.hierarchy,
          visions: prev.hierarchy.visions.map((vision, i) => 
            i === visionIndex 
              ? {
                  ...vision,
                  objectives: vision.objectives.map((objective, j) => 
                    j === objectiveIndex 
                      ? {
                          ...objective,
                          strategies: objective.strategies.map((strategy, k) => 
                            k === strategyIndex 
                              ? {
                                  ...strategy,
                                  projects: strategy.projects.map((project, l) => 
                                    l === projectIndex 
                                      ? { ...project, inchstones: [...(project.inchstones || []), newInchstone] }
                                      : project
                                  )
                                }
                              : strategy
                          )
                        }
                      : objective
                  )
                }
              : vision
          )
        }
      };
    });
  };

  // =====================================================
  // JSON EDITOR FUNCTIONS
  // =====================================================

  const toggleJsonEditor = () => {
    if (!showJsonEditor) {
      // Convert template to JSON when opening editor
      setJsonContent(JSON.stringify(template, null, 2));
    }
    setShowJsonEditor(!showJsonEditor);
  };
  // toggleJsonEditor is used indirectly via setActiveTab('json') - keeping it
  void toggleJsonEditor;

  // Update JSON content when switching to JSON tab
  useEffect(() => {
    if (activeTab === 'json') {
      setJsonContent(JSON.stringify(template, null, 2));
    }
  }, [activeTab, template]);

  // Extract projects when switching to timeline tab
  useEffect(() => {
    if (activeTab === 'timeline') {
      // Extract projects from template hierarchy
      const templateForExtraction = {
        id: editTemplateId || 'new-template',
        content: {
          template_metadata: template.template_metadata,
          hierarchy: template.hierarchy
        }
      };
      const extracted = extractProjectsFromHierarchy(templateForExtraction, template.template_metadata);
      setProjects(extracted);
    }
  }, [activeTab, template, editTemplateId]);

  const handleJsonChange = (newJson: string) => {
    setJsonContent(newJson);
  };

  const applyJsonChanges = () => {
    try {
      const parsedTemplate = JSON.parse(jsonContent);
      setTemplate(parsedTemplate);
      setShowJsonEditor(false);
      setError(null);
    } catch (err) {
      setError(t('templates.errors.invalidJson') || `Invalid JSON: ${err instanceof Error ? err.message : t('templates.errors.unknownError') || 'Unknown error'}`);
    }
  };

  // =====================================================
  // TEMPLATE SAVE
  // =====================================================

  // Internal save function that does the actual database save
  const performSave = async (showSuccessMessage: boolean = true, isAutosave: boolean = false): Promise<boolean> => {
    try {
      // Don't set loading state for autosave to avoid UI blocking
      if (!isAutosave) {
        setLoading(true);
        setError(null);
      }

      // Validate user authentication
      if (!user?.tenantId || !user?.id) {
        if (!isAutosave) {
          setError(t('templates.errors.userNotAuthenticated') || 'User not authenticated. Please log in again.');
        }
        return false;
      }

      // Validate required fields
      if (!template.template_metadata?.name?.trim()) {
        if (!isAutosave) {
          setError(t('templates.templateNameRequired'));
        }
        return false;
      }

      // If in roadmap mode, save as roadmap (create new or update existing)
      if (isRoadmapMode) {
        return await saveAsRoadmap(originalRoadmapId, showSuccessMessage, isAutosave);
      }

      // Otherwise, save as template (existing logic)
      if (isEditMode && editTemplateId) {
        // Update existing template
        // Preserve marketplace_visibility from original template - don't change it
        const updateData: any = {
          name: template.template_metadata?.name || '',
          description: template.template_metadata?.description || '',
          content: template // Send the entire template object as content
        };
        
        // Only include marketplace_visibility if it exists in the original template
        // This preserves the current visibility setting (DRAFT, PRIVATE, or PUBLIC)
        if (originalTemplate?.marketplace_visibility) {
          updateData.marketplace_visibility = originalTemplate.marketplace_visibility;
        }
        
        await templateService.updateTemplate(editTemplateId, updateData);
        
        if (showSuccessMessage && !isAutosave) {
          setSuccess(t('templates.templateSavedSuccessfully'));
        }
        return true;
      } else {
        // Create new template
        const newTemplate = await templateService.createHierarchicalTemplate(
          template,
          user.tenantId,
          user.id
        );
        
        // Update editTemplateId if this was a new template
        if (newTemplate.id) {
          // Update the URL to include the edit parameter
          navigate(`/templates/comprehensive-builder?edit=${newTemplate.id}`, { replace: true });
        }
        
        if (showSuccessMessage && !isAutosave) {
          setSuccess(t('templates.templateSavedSuccessfully'));
        }
        return true;
      }
    } catch (err) {
      console.error('❌ Error saving template:', err);
      if (!isAutosave) {
        setError(err instanceof Error ? err.message : t('templates.errorSavingTemplate'));
      }
      return false;
    } finally {
      if (!isAutosave) {
        setLoading(false);
      }
    }
  };

  // Save roadmap from template structure
  const saveAsRoadmap = async (roadmapId: string | null, showSuccessMessage: boolean = true, isAutosave: boolean = false, templateToSave?: any): Promise<boolean> => {
    try {
      // Use provided template or fall back to state
      const templateToUse = templateToSave || template;
      
      // Don't set loading state for autosave to avoid UI blocking
      if (!isAutosave) {
        setLoading(true);
        setError(null);
      }

      // Validate user authentication
      if (!user?.tenantId || !user?.id) {
        if (!isAutosave) {
          setError('User not authenticated. Please log in again.');
        }
        return false;
      }

      // Validate required fields
      if (!templateToUse.template_metadata?.name?.trim()) {
        if (!isAutosave) {
          setError('Please enter a roadmap name');
        }
        return false;
      }

      if (roadmapId) {
        // Update existing roadmap from template structure
        // Extract ALL projects with their start_month values to track positions
        const allProjects: Array<{id: string, name: string, start_month: number}> = [];
        for (const vision of templateToUse.hierarchy?.visions || []) {
          for (const objective of vision.objectives || []) {
            for (const strategy of objective.strategies || []) {
              for (const project of strategy.projects || []) {
                allProjects.push({
                  id: project.id || 'no-id',
                  name: project.title || project.name || 'unnamed',
                  start_month: (project as any)?.start_month ?? -1
                });
                // Check nested projects
                if (project.projects) {
                  for (const nestedProject of project.projects) {
                    allProjects.push({
                      id: nestedProject.id || 'no-id',
                      name: nestedProject.title || nestedProject.name || 'unnamed',
                      start_month: (nestedProject as any)?.start_month ?? -1
                    });
                  }
                }
              }
            }
          }
        }
        
        const response = await api.put(`/tenant/roadmaps/${roadmapId}/update-from-template-structure`, {
          templateStructure: templateToUse
        });

        if (response.data.success) {
          
          if (showSuccessMessage && !isAutosave) {
            setSuccess('Roadmap updated successfully!');
          }
          return true;
        } else {
          throw new Error(response.data.message || 'Failed to update roadmap');
        }
      } else {
        // Create new roadmap from template structure (no intermediate template save)
        const response = await api.post('/tenant/roadmaps/convert-from-template-structure', {
          templateStructure: templateToUse,
          roadmapName: templateToUse.template_metadata.name
        });

        if (response.data.success) {
          const newRoadmapId = response.data.data.roadmapId;
          // Update URL to roadmap edit mode
          navigate(`/tenant/roadmaps/builder?roadmap=${newRoadmapId}`, { replace: true });
          setOriginalRoadmapId(newRoadmapId);
          
          if (showSuccessMessage && !isAutosave) {
            setSuccess('Roadmap created successfully!');
          }
          return true;
        } else {
          throw new Error(response.data.message || 'Failed to create roadmap');
        }
      }
    } catch (err) {
      console.error('❌ [saveAsRoadmap] Error saving roadmap:', err);
      if (err instanceof Error) {
        console.error('  Error message:', err.message);
        console.error('  Error stack:', err.stack);
      }
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as any;
        console.error('  Response status:', axiosError.response?.status);
        console.error('  Response data:', axiosError.response?.data);
      }
      if (!isAutosave) {
        const errorMessage = err instanceof Error 
          ? err.message 
          : (err && typeof err === 'object' && 'response' in err && (err as any).response?.data?.message)
          ? (err as any).response.data.message
          : 'Failed to save roadmap';
        setError(errorMessage);
      }
      return false;
    } finally {
      if (!isAutosave) {
        setLoading(false);
      }
    }
  };

  // Public save function (for manual saves)
  const handleSave = async (showSuccessMessage: boolean = true) => {
    // If in roadmap mode, save as roadmap (create new or update existing)
    if (isRoadmapMode) {
      await saveAsRoadmap(originalRoadmapId, showSuccessMessage, false);
    } else {
      await performSave(showSuccessMessage, false);
    }
  };

  // Helper function to trigger autosave (used by both timeline and hierarchy drag-and-drop)
  const triggerAutosave = () => {
    // Clear any existing timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }

    // For roadmap mode, always autosave if we have a roadmap ID
    if (isRoadmapMode && originalRoadmapId) {
      // Set a new timeout to save after 1 second of no updates
      autosaveTimeoutRef.current = setTimeout(async () => {
        try {
          await saveAsRoadmap(originalRoadmapId, false, true); // Silent autosave
          autosaveTimeoutRef.current = null;
        } catch (error) {
          console.error('❌ Error autosaving roadmap:', error);
          autosaveTimeoutRef.current = null;
          // Don't show error to user for autosave failures - they can manually save
        }
      }, 1000); // Wait 1 second after last update before saving
    } else if (isEditMode && editTemplateId) {
      // Set a new timeout to save after 1 second of no updates
      autosaveTimeoutRef.current = setTimeout(async () => {
        try {
          await performSave(false, true); // Silent autosave
          autosaveTimeoutRef.current = null;
        } catch (error) {
          console.error('❌ Error autosaving template:', error);
          autosaveTimeoutRef.current = null;
          // Don't show error to user for autosave failures - they can manually save
        }
      }, 1000); // Wait 1 second after last update before saving
    } else if (!isEditMode && template.template_metadata?.name?.trim() && user?.tenantId && user?.id) {
      // For new templates, try to create and then autosave subsequent changes
      // This allows autosave to work even for new templates after they're created
      autosaveTimeoutRef.current = setTimeout(async () => {
        try {
          await performSave(false, true);
          // After first save, we'll be in edit mode for subsequent autosaves
          autosaveTimeoutRef.current = null;
        } catch (error) {
          console.error('❌ Error autosaving new template:', error);
          autosaveTimeoutRef.current = null;
        }
      }, 1000);
    }
  };

  // Helper function to trigger autosave with a specific template (used by timeline updates)
  // This ensures we use the updated template immediately, not waiting for React state to update
  const triggerAutosaveWithTemplate = (templateToSave: any) => {
    // Clear any existing timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }

    // For roadmap mode, always autosave if we have a roadmap ID
    if (isRoadmapMode && originalRoadmapId) {
      // Set a new timeout to save after 1 second of no updates
      autosaveTimeoutRef.current = setTimeout(async () => {
        try {
          // Save using the provided template (not the state, which might be stale)
          await saveAsRoadmap(originalRoadmapId, false, true, templateToSave);
          
          autosaveTimeoutRef.current = null;
        } catch (error) {
          console.error('❌ Error autosaving roadmap:', error);
          autosaveTimeoutRef.current = null;
          // Don't show error to user for autosave failures - they can manually save
        }
      }, 1000); // Wait 1 second after last update before saving
    } else {
      // For non-roadmap mode, use regular triggerAutosave
      triggerAutosave();
    }
  };

  // Extract projects from template hierarchy for visual timeline
  const extractProjectsForTimeline = (): RoadmapProject[] => {
    // Convert PureHierarchicalTemplate to the format expected by extractProjectsFromHierarchy
    const templateForExtraction = {
      id: editTemplateId || 'new-template',
      content: {
        template_metadata: template.template_metadata,
        hierarchy: template.hierarchy
      }
    };
    
    const metadata = template.template_metadata || {};
    const extractedProjects = extractProjectsFromHierarchy(templateForExtraction, metadata);
    
    // Add _path tracking for each project (needed for updates)
    let visionIndex = 0;
    let objectiveIndex = 0;
    let strategyIndex = 0;
    
    if (template.hierarchy?.visions) {
      template.hierarchy.visions.forEach((vision: any, vIdx: number) => {
        visionIndex = vIdx;
        vision.objectives?.forEach((objective: any, oIdx: number) => {
          objectiveIndex = oIdx;
          objective.strategies?.forEach((strategy: any, sIdx: number) => {
            strategyIndex = sIdx;
            
            if (strategy.projects && strategy.projects.length > 0) {
              strategy.projects.forEach((program: any, programIndex: number) => {
                // CRITICAL: Use real database ID if available, not generated ID
                const programId = program.id || program.existing_id || `${editTemplateId || 'new'}-program-${program.title}`;
                const programProject = extractedProjects.find(p => p.id === programId);
                if (programProject) {
                  // @ts-expect-error - custom property to track project location
                  programProject._path = { visionIndex, objectiveIndex, strategyIndex, programIndex, isProgram: true };
                }
                
                if (program.projects && program.projects.length > 0) {
                  program.projects.forEach((project: any, projectIndexInProgram: number) => {
                    const addPathRecursively = (proj: any, parentId: string, projectIndexInParent: number) => {
                      // CRITICAL: Use real database ID if available
                      const projId = proj.id || proj.existing_id || `${editTemplateId || 'new'}-project-${projectIndexInProgram}`;
                      const projProject = extractedProjects.find(p => p.id === projId && p.parent_id === parentId);
                      if (projProject) {
                        // @ts-expect-error - custom property to track project location
                        projProject._path = { visionIndex, objectiveIndex, strategyIndex, programIndex, projectIndexInParent };
                      }
                      
                      if (proj.projects && proj.projects.length > 0) {
                        proj.projects.forEach((subProj: any, subIndex: number) => {
                          addPathRecursively(subProj, projId, subIndex);
                        });
                      }
                    };
                    addPathRecursively(project, programId, projectIndexInProgram);
                  });
                }
              });
            }
            
            if (strategy.projects && strategy.projects.length > 0) {
              strategy.projects.forEach((project: any, projectIndexInStrategy: number) => {
                const addPathRecursively = (proj: any, parentId: string, projectIndexInStrategy: number) => {
                  // CRITICAL: Use real database ID if available
                  const projId = proj.id || proj.existing_id || `${editTemplateId || 'new'}-project-${projectIndexInStrategy}`;
                  const projProject = extractedProjects.find(p => p.id === projId && (p.parent_id === parentId || !p.parent_id));
                  if (projProject) {
                    // @ts-expect-error - custom property to track project location
                    projProject._path = { visionIndex, objectiveIndex, strategyIndex, projectIndexInStrategy };
                  }
                  
                  if (proj.projects && proj.projects.length > 0) {
                    proj.projects.forEach((subProj: any, subIndex: number) => {
                      addPathRecursively(subProj, projId, subIndex);
                    });
                  }
                };
                addPathRecursively(project, `${editTemplateId || 'new'}-strategy-${strategy.title}`, projectIndexInStrategy);
              });
            }
          });
        });
      });
    }
    
    return extractedProjects;
  };

  // Update projects when template changes
  useEffect(() => {
    if (template && template.hierarchy?.visions && template.hierarchy.visions.length > 0) {
      if (pendingUpdates.size === 0) {
        const extracted = extractProjectsForTimeline();
        setProjects(extracted);
      }
    } else {
      setProjects([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, pendingUpdates.size, editTemplateId]);

  // Helper function to find a project in the template hierarchy by ID and build its path
  const findProjectInHierarchy = (projectId: string): { project: any; path: any } | null => {
    if (!template.hierarchy?.visions) {
      return null;
    }
    
    for (let visionIndex = 0; visionIndex < template.hierarchy.visions.length; visionIndex++) {
      const vision = template.hierarchy.visions[visionIndex];
      if (!vision.objectives) continue;
      
      for (let objectiveIndex = 0; objectiveIndex < vision.objectives.length; objectiveIndex++) {
        const objective = vision.objectives[objectiveIndex];
        if (!objective.strategies) continue;
        
        for (let strategyIndex = 0; strategyIndex < objective.strategies.length; strategyIndex++) {
          const strategy = objective.strategies[strategyIndex];
          if (!strategy.projects) continue;
          
          // Search in top-level projects (programs or regular projects)
          for (let programIndex = 0; programIndex < strategy.projects.length; programIndex++) {
            const program = strategy.projects[programIndex];
            
            // Check if this program matches
            if (((program as any).id === projectId || (program as any).existing_id === projectId)) {
              return {
                project: program,
                path: { visionIndex, objectiveIndex, strategyIndex, programIndex, isProgram: true }
              };
            }
            
            // Check nested projects
            if (program.projects && Array.isArray(program.projects)) {
              for (let projectIndexInParent = 0; projectIndexInParent < program.projects.length; projectIndexInParent++) {
                const nestedProject = program.projects[projectIndexInParent];
                if (((nestedProject as any).id === projectId || (nestedProject as any).existing_id === projectId)) {
                  return {
                    project: nestedProject,
                    path: { visionIndex, objectiveIndex, strategyIndex, programIndex, projectIndexInParent }
                  };
                }
              }
            }
          }
          
          // Also check if it's a direct project (not in a program)
          for (let projectIndexInStrategy = 0; projectIndexInStrategy < strategy.projects.length; projectIndexInStrategy++) {
            const directProject = strategy.projects[projectIndexInStrategy];
            if (((directProject as any).id === projectId || (directProject as any).existing_id === projectId)) {
              return {
                project: directProject,
                path: { visionIndex, objectiveIndex, strategyIndex, projectIndexInStrategy }
              };
            }
          }
        }
      }
    }
    
    return null;
  };

  // Handle timeline updates (drag and drop)
  const handleTimelineUpdate = async (projectId: string, updates: { estimated_duration_months?: number; start_month?: number; parent_id?: string | null; strategy_title?: string; strategy_description?: string }) => {
    
    // Find the project directly in the template hierarchy by ID
    const found = findProjectInHierarchy(projectId);
    if (!found) {
      console.error('❌ [handleTimelineUpdate] Project not found in template hierarchy:', projectId);
      // Log available project IDs for debugging
      const availableIds: string[] = [];
      template.hierarchy?.visions?.forEach((vision: any) => {
        vision.objectives?.forEach((objective: any) => {
          objective.strategies?.forEach((strategy: any) => {
            strategy.projects?.forEach((proj: any) => {
              if (proj.id) availableIds.push(proj.id);
              if (proj.existing_id) availableIds.push(proj.existing_id);
              proj.projects?.forEach((nested: any) => {
                if (nested.id) availableIds.push(nested.id);
                if (nested.existing_id) availableIds.push(nested.existing_id);
              });
            });
          });
        });
      });
      console.error('Available project IDs:', availableIds);
      return;
    }

    const { project: projectToUpdate, path } = found;
    const previousTemplate = JSON.parse(JSON.stringify(template));
    const previousProjects = [...projects];

    // Mark as pending
    setPendingUpdates(prev => new Set(prev).add(projectId));

    try {
      // projectToUpdate and path are already found by findProjectInHierarchy
      // Verify we have the strategy
      const vision = template.hierarchy.visions[path.visionIndex];
      const objective = vision?.objectives?.[path.objectiveIndex];
      const strategy = objective?.strategies?.[path.strategyIndex];
      
      if (!strategy || !projectToUpdate) {
        console.error('❌ [handleTimelineUpdate] Strategy or project not found. Path:', path, 'ProjectId:', projectId);
        return;
      }
      
      // Store original values for position tracking
      const originalStartMonth = (projectToUpdate as any).start_month || 0;
      const monthDelta = updates.start_month !== undefined ? (updates.start_month - originalStartMonth) : 0;
      
      // Update the project directly (immutable update will happen via setTemplate)
      if (updates.start_month !== undefined) {
        (projectToUpdate as any).start_month = updates.start_month;
      }
      if (updates.estimated_duration_months !== undefined) {
        projectToUpdate.estimated_duration_months = updates.estimated_duration_months;
      }
      
      // If this is a program and we're MOVING it, update all children
      const isProgram = projectToUpdate.projects && Array.isArray(projectToUpdate.projects) && projectToUpdate.projects.length > 0;
      const isMoving = monthDelta !== 0 && updates.start_month !== undefined;
      
      if (isProgram && isMoving && projectToUpdate.projects && Array.isArray(projectToUpdate.projects)) {
        const updateChildrenRecursively = (children: any[]) => {
          children.forEach((child: any) => {
            if (child.start_month !== undefined) {
              child.start_month = Math.max(0, (child.start_month || 0) + monthDelta);
            }
            if (child.projects && Array.isArray(child.projects)) {
              updateChildrenRecursively(child.projects);
            }
          });
        };
        updateChildrenRecursively(projectToUpdate.projects);
      }
      
      // Handle strategy changes (moving projects/programs to a different strategy)
      if (updates.strategy_title) {
        // Find the target strategy
        let targetStrategy: any = null;
        
        template.hierarchy.visions.forEach((vision: any) => {
          vision.objectives?.forEach((objective: any) => {
            objective.strategies?.forEach((strategy: any) => {
              if (strategy.title === updates.strategy_title) {
                targetStrategy = strategy;
              }
            });
          });
        });
        
        if (targetStrategy) {
          // Remove from current location
          if (path.isProgram && path.programIndex !== undefined && strategy.projects) {
            strategy.projects.splice(path.programIndex, 1);
          } else if (path.programIndex !== undefined && path.projectIndexInParent !== undefined && strategy.projects) {
            const program = strategy.projects[path.programIndex];
            if (program?.projects) {
              program.projects.splice(path.projectIndexInParent, 1);
            }
          } else if (path.projectIndexInStrategy !== undefined && strategy.projects) {
            strategy.projects.splice(path.projectIndexInStrategy, 1);
          }
          
          // Add to target strategy's root
          if (!updates.parent_id) {
            if (!targetStrategy.projects) {
              targetStrategy.projects = [];
            }
            targetStrategy.projects.push(projectToUpdate);
          }
        }
      }
      
      // Handle parent_id changes (nesting/moving projects)
      if (updates.parent_id !== undefined) {
        // Find target parent and move project
        // This is complex, so we'll handle basic cases for now
        // Full implementation would mirror TemplatePreviewModal's logic
      }

      // CRITICAL: Create a new object reference for React state update
      // We've mutated the nested objects in place, but React needs a new top-level reference
      // The nested objects (hierarchy, visions, etc.) are the same references, so mutations are preserved
      const updatedTemplate = { ...template };
      
      setTemplate(updatedTemplate);

      // Update projects state - this will re-extract projects with updated start_month values
      const updatedProjects = extractProjectsForTimeline();
      setProjects(updatedProjects);

      // Clear pending flag
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });

      // CRITICAL: Use updatedTemplate (not template) for autosave
      // updatedTemplate is a shallow copy, but the nested objects are the same references
      // So it has all our mutations, but with a new top-level reference for React
      // We MUST use updatedTemplate, not template, because template is the old state from closure
      setTimeout(() => {
        triggerAutosaveWithTemplate(updatedTemplate);
      }, 100);
    } catch (error) {
      console.error('Error updating timeline:', error);
      // Rollback on error
      setTemplate(previousTemplate);
      setProjects(previousProjects);
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <PageContextWrapper context={pageContext}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* First Line: Title and Action Buttons */}
          <div className="flex justify-between items-center py-4 border-b">
            <div>
              <h1 className="text-2xl font-bold text-[var(--color-secondary-text)]">
                {isRoadmapMode 
                  ? (originalRoadmapId ? t('templates.editMyRoadmap') || 'Edit My Roadmap' : 'Create Roadmap')
                  : isEditMode 
                  ? t('templates.editRoadmapTemplate') 
                  : t('templates.templateBuilder') || 'Template Builder'
                }
              </h1>
              <p className="text-[var(--color-surface-text)]">
                {isRoadmapMode
                  ? (originalRoadmapId ? t('templates.editMyRoadmapDescription') || 'Edit your own business roadmaps' : t('templates.createRoadmapDescription') || 'Create a new roadmap using the unified builder interface')
                  : isEditMode 
                  ? t('templates.editTemplateDescription') 
                  : t('templates.createTemplateDescription')
                }
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  if (isRoadmapMode) {
                    navigate('/tenant/roadmaps');
                  } else {
                    navigate('/templates/manage');
                  }
                }}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                {isRoadmapMode ? 'Back to Roadmaps' : t('templates.backToTemplates')}
              </button>
              {isRoadmapMode ? (
                <button
                  onClick={() => saveAsRoadmap(originalRoadmapId, true, false)}
                  disabled={loading || !template.template_metadata?.name?.trim() || !user?.tenantId || !user?.id}
                  className={`${getContextButtonClasses(pageContext)} px-4 py-2 rounded-lg transition-colors disabled:opacity-50`}
                  title={
                    !user?.tenantId || !user?.id 
                      ? 'Please log in to save'
                      : !template.template_metadata?.name?.trim()
                      ? 'Please enter a roadmap name'
                      : originalRoadmapId ? 'Save Roadmap' : 'Create Roadmap'
                  }
                >
                  {loading ? (originalRoadmapId ? 'Saving...' : 'Creating...') : (originalRoadmapId ? 'Save Roadmap' : 'Create Roadmap')}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => handleSave()}
                    disabled={loading || !template.template_metadata?.name?.trim() || !user?.tenantId || !user?.id}
                    className={`${getContextButtonClasses(pageContext)} px-4 py-2 rounded-lg transition-colors disabled:opacity-50`}
                    title={
                      !user?.tenantId || !user?.id 
                        ? t('templates.pleaseLogInToSave')
                        : !template.template_metadata?.name?.trim()
                        ? t('templates.pleaseEnterTemplateName')
                        : t('templates.saveTemplate')
                    }
                  >
                    {loading ? t('templates.saving') : t('templates.save')}
                  </button>
                  {!isEditMode && (
                    <button
                      onClick={() => saveAsRoadmap(null, true, false)}
                      disabled={loading || !template.template_metadata?.name?.trim() || !user?.tenantId || !user?.id}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      title={
                        !user?.tenantId || !user?.id 
                          ? 'Please log in to save'
                          : !template.template_metadata?.name?.trim()
                          ? 'Please enter a roadmap name'
                          : 'Save as Roadmap (no intermediate template save)'
                      }
                    >
                      {loading ? 'Saving...' : 'Save as Roadmap'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Second Line: Tabs */}
          <div className="flex space-x-1 py-2">
            <button
              onClick={() => setActiveTab('metadata')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'metadata'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('templates.metadata')}
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'timeline'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('templates.timeline')}
            </button>
            <button
              onClick={() => setActiveTab('builder')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'builder'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('templates.roadmapBuilder')}
            </button>
            {/* JSON Editor tab - available for templates and roadmaps (read-only for existing roadmaps) */}
            <button
              onClick={() => setActiveTab('json')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'json'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('templates.jsonEditor')}
              {isRoadmapMode && editRoadmapId && (
                <span className="ml-2 text-xs">(Read-only)</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Template Metadata Tab */}
        {activeTab === 'metadata' && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">{t('templates.templateMetadata')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">{t('templates.name')}</label>
              <input
                type="text"
                value={template.template_metadata?.name || ''}
                onChange={(e) => setTemplate(prev => ({
                  ...prev,
                  template_metadata: { 
                    ...(prev.template_metadata || {
                      name: t('templates.newTemplate'),
                      description: '',
                      industry: 'General',
                      functional_area: 'General',
                      complexity_level: 1,
                      estimated_duration_months: 12,
                      tags: [],
                      version: '1.0',
                      created_by: user?.id || '',
                      created_at: new Date().toISOString()
                    }),
                    name: e.target.value 
                  }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('templates.templateNamePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">{t('templates.industry')}</label>
              <select
                value={template.template_metadata?.industry || 'General'}
                onChange={(e) => setTemplate(prev => ({
                  ...prev,
                  template_metadata: { 
                    ...(prev.template_metadata || {
                      name: t('templates.newTemplate'),
                      description: '',
                      industry: 'General',
                      functional_area: 'General',
                      complexity_level: 1,
                      estimated_duration_months: 12,
                      tags: [],
                      version: '1.0',
                      created_by: user?.id || '',
                      created_at: new Date().toISOString()
                    }),
                    industry: e.target.value 
                  }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {industryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">{t('templates.description')}</label>
              <textarea
                value={template.template_metadata?.description || ''}
                onChange={(e) => setTemplate(prev => ({
                  ...prev,
                  template_metadata: { 
                    ...(prev.template_metadata || {
                      name: t('templates.newTemplate'),
                      description: '',
                      industry: 'General',
                      functional_area: 'General',
                      complexity_level: 1,
                      estimated_duration_months: 12,
                      tags: [],
                      version: '1.0',
                      created_by: user?.id || '',
                      created_at: new Date().toISOString()
                    }),
                    description: e.target.value 
                  }
                }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('templates.templateDescriptionPlaceholder') || 'Template description'}
              />
            </div>
            {/* Additional metadata fields - available in both template and roadmap mode */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                {t('templates.transformationType') || 'Transformation Type'}
              </label>
              <select
                value={template.template_metadata?.transformation_type || ''}
                onChange={(e) => setTemplate(prev => ({
                  ...prev,
                  template_metadata: { 
                    ...(prev.template_metadata || {
                      name: t('templates.newTemplate'),
                      description: '',
                      industry: 'General',
                      functional_area: 'General',
                      complexity_level: 1,
                      estimated_duration_months: 12,
                      tags: [],
                      version: '1.0',
                      created_by: user?.id || '',
                      created_at: new Date().toISOString()
                    }),
                    transformation_type: e.target.value || undefined 
                  }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('templates.selectTransformationType') || 'Select Transformation Type (Optional)'}</option>
                <option value="Digital Transformation">{t('transformation.types.digitalTransformation') || 'Digital Transformation'}</option>
                <option value="Process Transformation">{t('transformation.types.processTransformation') || 'Process Transformation'}</option>
                <option value="Organizational Transformation">{t('transformation.types.organizationalTransformation') || 'Organizational Transformation'}</option>
                <option value="Technology Transformation">{t('transformation.types.technologyTransformation') || 'Technology Transformation'}</option>
                <option value="Cultural Transformation">{t('transformation.types.culturalTransformation') || 'Cultural Transformation'}</option>
                <option value="Assessment & Planning">{t('transformation.types.assessmentPlanning') || 'Assessment & Planning'}</option>
                <option value="Business Case Development">{t('transformation.types.businessCaseDevelopment') || 'Business Case Development'}</option>
                <option value="Platform Selection">{t('transformation.types.platformSelection') || 'Platform Selection'}</option>
                <option value="Data Migration">{t('transformation.types.dataMigration') || 'Data Migration'}</option>
                <option value="Change Management">{t('transformation.types.changeManagement') || 'Change Management'}</option>
                <option value="System Integration">{t('transformation.types.systemIntegration') || 'System Integration'}</option>
                <option value="Collaboration Platform">{t('transformation.types.collaborationPlatform') || 'Collaboration Platform'}</option>
                <option value="Process Automation">{t('transformation.types.processAutomation') || 'Process Automation'}</option>
                <option value="AI Integration">{t('transformation.types.aiIntegration') || 'AI Integration'}</option>
                <option value="Digital Twin">{t('transformation.types.digitalTwin') || 'Digital Twin'}</option>
                <option value="Automation">{t('transformation.types.automation') || 'Automation'}</option>
                <option value="ESG Integration">{t('transformation.types.esgIntegration') || 'ESG Integration'}</option>
                <option value="Circular Economy">{t('transformation.types.circularEconomy') || 'Circular Economy'}</option>
                <option value="Customization Platform">{t('transformation.types.customizationPlatform') || 'Customization Platform'}</option>
              </select>
            </div>
            {isRoadmapMode && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                  {t('tenantRoadmaps.status') || 'Status'}
                </label>
                <select
                  value={template.template_metadata?.status || 'ACTIVE'}
                  onChange={(e) => setTemplate(prev => ({
                    ...prev,
                    template_metadata: { 
                      ...(prev.template_metadata || {}),
                      status: e.target.value as 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | undefined
                    }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ACTIVE">{t('tenantRoadmaps.statusActive') || 'Active'}</option>
                  <option value="INACTIVE">{t('tenantRoadmaps.statusInactive') || 'Inactive'}</option>
                  <option value="ARCHIVED">{t('tenantRoadmaps.statusArchived') || 'Archived'}</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                {t('templates.estimatedCostRangeMin') || 'Estimated Cost Range (Min) $'}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={template.template_metadata?.estimated_cost_range_min || ''}
                onChange={(e) => setTemplate(prev => ({
                  ...prev,
                  template_metadata: { 
                    ...(prev.template_metadata || {
                      name: t('templates.newTemplate'),
                      description: '',
                      industry: 'General',
                      functional_area: 'General',
                      complexity_level: 1,
                      estimated_duration_months: 12,
                      tags: [],
                      version: '1.0',
                      created_by: user?.id || '',
                      created_at: new Date().toISOString()
                    }),
                    estimated_cost_range_min: e.target.value ? parseFloat(e.target.value) : undefined
                  }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                {t('templates.estimatedCostRangeMax') || 'Estimated Cost Range (Max) $'}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={template.template_metadata?.estimated_cost_range_max || ''}
                onChange={(e) => setTemplate(prev => ({
                  ...prev,
                  template_metadata: { 
                    ...(prev.template_metadata || {
                      name: t('templates.newTemplate'),
                      description: '',
                      industry: 'General',
                      functional_area: 'General',
                      complexity_level: 1,
                      estimated_duration_months: 12,
                      tags: [],
                      version: '1.0',
                      created_by: user?.id || '',
                      created_at: new Date().toISOString()
                    }),
                    estimated_cost_range_max: e.target.value ? parseFloat(e.target.value) : undefined
                  }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
          </div>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">{t('templates.visualTimeline')}</h2>
            {projects.length > 0 ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  {t('templates.timelineInstructions') || 'Drag projects to move them, drag edges to resize, and drop on other projects to nest them. Drop on a strategy container to move to that strategy.'}
                </p>
                {isRoadmapMode && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Timeline Changes:</strong> When you adjust the timeline (move projects, change durations), 
                      the changes are saved automatically. When you click &quot;Save as Roadmap&quot; or &quot;Update Roadmap&quot;, 
                      all timeline adjustments (start months, durations, project positions) will be saved to the database 
                      and reflected in the Strategic Alignment and Transformations modules.
                    </p>
                  </div>
                )}
                <VisualRoadmapTimeline
                  projects={projects}
                  roadmapName={template.template_metadata?.name || t('templates.template') || 'Template'}
                  totalDuration={Math.max(36, ...projects.map(p => (p.start_month || 0) + p.estimated_duration_months))}
                  isEditable={true}
                  onTimelineUpdate={handleTimelineUpdate}
                />
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>{t('templates.noProjectsFound')}</p>
                <p className="text-sm mt-2">{t('templates.addItemsToSeeTimeline')}</p>
              </div>
            )}
          </div>
        )}

        {/* Roadmap Builder Tab */}
        {activeTab === 'builder' && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)]">{t('templates.templateHierarchy')}</h2>
            <button
              onClick={addVision}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              {t('templates.addVision')}
            </button>
          </div>
          
          {/* Information message for roadmap mode */}
          {isRoadmapMode && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Visions, strategies, objectives, and projects can be edited within the Strategic Alignment module. 
                Project details can be edited from inside &quot;Transformations&quot;.
              </p>
            </div>
          )}

          {/* Visions */}
            {template.hierarchy.visions.map((vision, visionIndex) => {
              const visionIsReadOnly = isReadOnly(vision);
              return (
              <div 
                key={visionIndex} 
                className={`border border-blue-200 rounded-lg p-4 mb-4 bg-blue-50 ${visionIsReadOnly ? 'cursor-default' : 'cursor-move'}`}
                draggable={!visionIsReadOnly}
                onDragStart={visionIsReadOnly ? undefined : (e) => {
                  e.stopPropagation();
                  handleDragStart(e, 'vision', [visionIndex], vision);
                }}
                onDragOver={visionIsReadOnly ? undefined : (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDragEnd={visionIsReadOnly ? undefined : handleDragEnd}
                onDrop={visionIsReadOnly ? undefined : (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDrop(e, [visionIndex], 'vision');
                }}
              >
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center space-x-2">
                  {!visionIsReadOnly && (
                    <span 
                      className="text-blue-600 cursor-grab active:cursor-grabbing select-none"
                      onMouseDown={(e) => e.stopPropagation()}
                    >⋮⋮</span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCollapse(`vision-${visionIndex}`);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title={isCollapsed(`vision-${visionIndex}`) ? t('templates.expandVision') : t('templates.collapseVision')}
                  >
                    {isCollapsed(`vision-${visionIndex}`) ? '▶' : '▼'}
                  </button>
                  <h3 className="text-lg font-medium text-blue-900">
                    {t('templates.visionLabel')}: {vision.title}
                    {visionIsReadOnly && <span className="text-xs text-gray-500 ml-2">(Read-only)</span>}
                  </h3>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      addObjective(visionIndex);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                  >
                    {t('templates.addObjective')}
                  </button>
                  {!visionIsReadOnly && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(() => removeVision(visionIndex), 'Vision');
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="bg-gray-200 text-red-600 p-1 rounded hover:bg-gray-300"
                      title={t('templates.removeVision')}
                    >
                      [-]
                    </button>
                  )}
                </div>
              </div>

              {/* Vision Details */}
              {!isCollapsed(`vision-${visionIndex}`) && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <input
                  type="text"
                  value={vision.title}
                  onChange={(e) => updateVision(visionIndex, { ...vision, title: e.target.value })}
                  onMouseDown={(e) => e.stopPropagation()}
                  readOnly={visionIsReadOnly}
                  disabled={visionIsReadOnly}
                  className={`px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 ${visionIsReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder={t('templates.visionTitlePlaceholder')}
                />
                <input
                  type="text"
                  value={vision.description}
                  onChange={(e) => updateVision(visionIndex, { ...vision, description: e.target.value })}
                  onMouseDown={(e) => e.stopPropagation()}
                  readOnly={visionIsReadOnly}
                  disabled={visionIsReadOnly}
                  className={`px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 ${visionIsReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder={t('templates.visionDescriptionPlaceholder')}
                />
              </div>

              {/* Objectives */}
              {vision.objectives.map((objective, objectiveIndex) => {
                const objectiveIsReadOnly = isReadOnly(objective);
                return (
                <div 
                  key={objectiveIndex} 
                  className={`border border-green-200 rounded-lg p-3 mb-3 bg-green-50 ml-4 ${objectiveIsReadOnly ? 'cursor-default' : 'cursor-move'}`}
                  draggable={!objectiveIsReadOnly}
                  onDragStart={objectiveIsReadOnly ? undefined : (e) => {
                    e.stopPropagation();
                    handleDragStart(e, 'objective', [visionIndex, objectiveIndex], objective);
                  }}
                  onDragOver={objectiveIsReadOnly ? undefined : (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDragEnd={objectiveIsReadOnly ? undefined : handleDragEnd}
                  onDrop={objectiveIsReadOnly ? undefined : (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(e, [visionIndex, objectiveIndex], 'objective');
                  }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center space-x-2">
                      {!objectiveIsReadOnly && (
                        <span 
                          className="text-green-600 cursor-grab active:cursor-grabbing select-none"
                          onMouseDown={(e) => e.stopPropagation()}
                        >⋮⋮</span>
                      )}
                      <button
                        onClick={() => toggleCollapse(`objective-${visionIndex}-${objectiveIndex}`)}
                        className="text-green-600 hover:text-green-800 p-1"
                        title={isCollapsed(`objective-${visionIndex}-${objectiveIndex}`) ? t('templates.expandObjective') : t('templates.collapseObjective')}
                      >
                        {isCollapsed(`objective-${visionIndex}-${objectiveIndex}`) ? '▶' : '▼'}
                      </button>
                      <h4 className="text-md font-medium text-green-900">
                        {t('templates.objectiveLabel')}: {objective.title}
                        {objectiveIsReadOnly && <span className="text-xs text-gray-500 ml-2">(Read-only)</span>}
                      </h4>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => addStrategy(visionIndex, objectiveIndex)}
                        className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                      >
                        {t('templates.addStrategy')}
                      </button>
                      {!objectiveIsReadOnly && (
                        <button
                          onClick={() => confirmDelete(() => removeObjective(visionIndex, objectiveIndex), 'Objective')}
                          className="bg-gray-200 text-red-600 p-1 rounded hover:bg-gray-300"
                          title={t('templates.removeObjective')}
                        >
                          [-]
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Objective Details */}
                  {!isCollapsed(`objective-${visionIndex}-${objectiveIndex}`) && (
                    <>
                      <div className="grid grid-cols-1 gap-2 mb-3">
                    <AlignmentTypeahead
                      value={objective.kpi || ''}
                      onChange={(name) =>
                        updateObjective(
                          visionIndex,
                          objectiveIndex,
                          applyObjectiveKpiFields(objective, { kpi: name })
                        )
                      }
                      options={platformKpis.map((kpi) => ({ id: kpi.name, label: kpi.name }))}
                      placeholder={t('alignment.objectives.chooseKPI')}
                      disabled={objectiveIsReadOnly}
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={objective.value_type || DEFAULT_OBJECTIVE_VALUE_TYPE}
                        onChange={(e) =>
                          updateObjective(
                            visionIndex,
                            objectiveIndex,
                            applyObjectiveKpiFields(objective, {
                              value_type: e.target.value as ObjectiveValueType
                            })
                          )
                        }
                        disabled={objectiveIsReadOnly}
                        options={[
                          { value: 'TARGET_LEVEL', label: t('alignment.objectives.valueTypes.targetLevel') },
                          { value: 'TARGET_IMPROVEMENT', label: t('alignment.objectives.valueTypes.targetImprovement') },
                          { value: 'TARGET_REDUCTION', label: t('alignment.objectives.valueTypes.targetReduction') }
                        ]}
                      />
                      <Input
                        inputMode="decimal"
                        value={objective.kpi_value ?? ''}
                        onChange={(e) =>
                          updateObjective(
                            visionIndex,
                            objectiveIndex,
                            applyObjectiveKpiFields(objective, {
                              kpi_value: parseObjectiveKpiInput(e.target.value)
                            })
                          )
                        }
                        disabled={objectiveIsReadOnly}
                        placeholder={t('alignment.objectives.kpiValue')}
                      />
                      <Input
                        type="number"
                        value={objective.year ?? ''}
                        onChange={(e) =>
                          updateObjective(
                            visionIndex,
                            objectiveIndex,
                            applyObjectiveKpiFields(objective, {
                              year: e.target.value === '' ? null : Number(e.target.value)
                            })
                          )
                        }
                        disabled={objectiveIsReadOnly}
                        placeholder={t('alignment.objectives.year')}
                      />
                    </div>
                    <input
                      type="text"
                      value={objective.title}
                      readOnly
                      disabled
                      className="px-2 py-1 border border-gray-300 rounded text-sm bg-gray-100 cursor-not-allowed"
                      placeholder={t('alignment.objectives.generatedName')}
                    />
                    <textarea
                      value={objective.description || ''}
                      onChange={(e) => updateObjective(visionIndex, objectiveIndex, { ...objective, description: e.target.value })}
                      readOnly={objectiveIsReadOnly}
                      disabled={objectiveIsReadOnly}
                      className={`px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 ${objectiveIsReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder={t('alignment.objectives.formLabels.description')}
                      rows={2}
                    />
                  </div>

                  {/* Strategies */}
                  {objective.strategies.map((strategy, strategyIndex) => {
                    const strategyIsReadOnly = isReadOnly(strategy);
                    return (
                    <div 
                      key={strategyIndex} 
                      className="border border-purple-200 rounded-lg p-2 mb-2 bg-purple-50 ml-4"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const dragged = draggedItemRef.current;
                        if (dragged?.type === 'project') {
                          e.dataTransfer.dropEffect = 'move';
                          (e.currentTarget as HTMLElement).classList.add('ring-2', 'ring-blue-400');
                        } else {
                          e.dataTransfer.dropEffect = 'none';
                        }
                      }}
                      onDragLeave={(e) => {
                        (e.currentTarget as HTMLElement).classList.remove('ring-2', 'ring-blue-400');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        (e.currentTarget as HTMLElement).classList.remove('ring-2', 'ring-blue-400');
                        // Allow dropping projects on strategy container
                        const dragged = draggedItemRef.current;
                        if (dragged?.type === 'project') {
                          handleDrop(e, [visionIndex, objectiveIndex, strategyIndex], 'strategy');
                        }
                      }}
                    >
                      <div 
                        className={`flex justify-between items-center mb-2 ${strategyIsReadOnly ? 'cursor-default' : 'cursor-move'}`}
                        draggable={!strategyIsReadOnly}
                        onDragStart={strategyIsReadOnly ? undefined : (e) => {
                          e.stopPropagation();
                          handleDragStart(e, 'strategy', [visionIndex, objectiveIndex, strategyIndex], strategy);
                        }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const dragged = draggedItemRef.current;
                            if (dragged?.type === 'strategy') {
                              e.dataTransfer.dropEffect = 'move';
                            } else {
                              e.dataTransfer.dropEffect = 'none';
                            }
                          }}
                          onDragEnd={handleDragEnd}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Only allow reordering strategies (same type)
                            const dragged = draggedItemRef.current;
                            if (dragged?.type === 'strategy') {
                              handleDrop(e, [visionIndex, objectiveIndex, strategyIndex], 'strategy');
                            }
                          }}
                      >
                        <div className="flex items-center space-x-2">
                          {!strategyIsReadOnly && (
                            <span 
                              className="text-purple-600 cursor-grab active:cursor-grabbing select-none"
                              onMouseDown={(e) => e.stopPropagation()}
                            >⋮⋮</span>
                          )}
                          <button
                            onClick={() => toggleCollapse(`strategy-${visionIndex}-${objectiveIndex}-${strategyIndex}`)}
                            className="text-purple-600 hover:text-purple-800 p-1"
                            title={isCollapsed(`strategy-${visionIndex}-${objectiveIndex}-${strategyIndex}`) ? t('templates.expandStrategy') : t('templates.collapseStrategy')}
                          >
                            {isCollapsed(`strategy-${visionIndex}-${objectiveIndex}-${strategyIndex}`) ? '▶' : '▼'}
                          </button>
                          <h5 className="text-sm font-medium text-purple-900">
                            {t('templates.strategyLabel')}: {strategy.title}
                            {strategyIsReadOnly && <span className="text-xs text-gray-500 ml-2">(Read-only)</span>}
                          </h5>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => addProject(visionIndex, objectiveIndex, strategyIndex)}
                            className="bg-purple-600 text-white px-2 py-1 rounded text-xs hover:bg-purple-700"
                          >
                            {t('templates.addProject')}
                          </button>
                          {!strategyIsReadOnly && (
                            <button
                              onClick={() => confirmDelete(() => removeStrategy(visionIndex, objectiveIndex, strategyIndex), 'Strategy')}
                              className="bg-gray-200 text-red-600 p-1 rounded hover:bg-gray-300"
                              title={t('templates.removeStrategy')}
                            >
                              [-]
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Strategy Details */}
                      {!isCollapsed(`strategy-${visionIndex}-${objectiveIndex}-${strategyIndex}`) && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                        <input
                          type="text"
                          value={strategy.title}
                          onChange={(e) => updateStrategy(visionIndex, objectiveIndex, strategyIndex, { ...strategy, title: e.target.value })}
                          readOnly={strategyIsReadOnly}
                          disabled={strategyIsReadOnly}
                          className={`px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 ${strategyIsReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          placeholder={t('templates.strategyTitlePlaceholder')}
                        />
                        <input
                          type="text"
                          value={strategy.description}
                          onChange={(e) => updateStrategy(visionIndex, objectiveIndex, strategyIndex, { ...strategy, description: e.target.value })}
                          readOnly={strategyIsReadOnly}
                          disabled={strategyIsReadOnly}
                          className={`px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 ${strategyIsReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          placeholder={t('templates.strategyDescriptionPlaceholder')}
                        />
                      </div>

                      {/* Projects - Recursive rendering with nested project support */}
                      {strategy.projects.map((project, projectIndex) => {
                        // Recursive component to render projects and nested projects
                        const renderProject = (proj: PureProject, projIndex: number, nestedPath: number[] = [], depth: number = 0): JSX.Element => {
                          const isProgram = (proj.projects && Array.isArray(proj.projects) && proj.projects.length > 0);
                          const collapseKey = `project-${visionIndex}-${objectiveIndex}-${strategyIndex}-${projIndex}${nestedPath.length > 0 ? `-${nestedPath.join('-')}` : ''}`;
                          const indentClass = depth > 0 ? `ml-${(depth + 1) * 4}` : 'ml-4';
                          const projectIsReadOnly = isReadOnly(proj);
                          
                          return (
                            <div 
                              key={`${projIndex}-${nestedPath.join('-')}`} 
                              className={`border border-orange-200 rounded-lg p-2 mb-2 bg-orange-50 ${indentClass} ${projectIsReadOnly ? 'cursor-default' : 'cursor-move'}`}
                              draggable={!projectIsReadOnly}
                              onDragStart={projectIsReadOnly ? undefined : (e) => {
                                e.stopPropagation();
                                handleDragStart(e, 'project', [visionIndex, objectiveIndex, strategyIndex, projIndex, ...nestedPath], proj);
                              }}
                              onDragOver={projectIsReadOnly ? undefined : (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const dragged = draggedItemRef.current;
                                if (dragged) {
                                  e.dataTransfer.dropEffect = 'move';
                                  (e.currentTarget as HTMLElement).classList.add('ring-2', 'ring-green-400');
                                } else {
                                  e.dataTransfer.dropEffect = 'none';
                                }
                              }}
                              onDragLeave={projectIsReadOnly ? undefined : (e) => {
                                (e.currentTarget as HTMLElement).classList.remove('ring-2', 'ring-green-400');
                              }}
                              onDragEnd={projectIsReadOnly ? undefined : handleDragEnd}
                              onDrop={projectIsReadOnly ? undefined : (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                (e.currentTarget as HTMLElement).classList.remove('ring-2', 'ring-green-400');
                                handleDrop(e, [visionIndex, objectiveIndex, strategyIndex, projIndex, ...nestedPath], 'project');
                              }}
                            >
                              <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center space-x-2">
                                  {!projectIsReadOnly && (
                                    <span 
                                      className="text-orange-600 cursor-grab active:cursor-grabbing select-none"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >⋮⋮</span>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleCollapse(collapseKey);
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    className="text-orange-600 hover:text-orange-800 p-1"
                                    title={isCollapsed(collapseKey) ? t('templates.expandProject') : t('templates.collapseProject')}
                                  >
                                    {isCollapsed(collapseKey) ? '▶' : '▼'}
                                  </button>
                                  <h6 className="text-sm font-medium text-orange-900">
                                    {isProgram ? '📦 PROGRAM' : '🚀 PROJECT'}: {proj.title}
                                    {projectIsReadOnly && <span className="text-xs text-gray-500 ml-2">(Read-only)</span>}
                                  </h6>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addNestedProject(visionIndex, objectiveIndex, strategyIndex, projIndex, nestedPath);
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    className="bg-orange-600 text-white px-2 py-1 rounded text-xs hover:bg-orange-700"
                                    title={t('templates.addProject') || 'Add Project'}
                                  >
                                    + Project
                                  </button>
                                  {!projectIsReadOnly && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        confirmDelete(() => removeProject(visionIndex, objectiveIndex, strategyIndex, projIndex, nestedPath.length > 0 ? nestedPath : undefined), 'Project');
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      className="bg-gray-200 text-red-600 p-1 rounded hover:bg-gray-300"
                                      title={t('templates.removeProject')}
                                    >
                                      [-]
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Project Details */}
                              {!isCollapsed(collapseKey) && (
                                <>
                                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                                    <div className="relative group">
                                      <input
                                        type="text"
                                        value={proj.title}
                                        onChange={(e) => updateProject(visionIndex, objectiveIndex, strategyIndex, projIndex, { ...proj, title: e.target.value }, nestedPath.length > 0 ? nestedPath : undefined)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        readOnly={projectIsReadOnly}
                                        disabled={projectIsReadOnly}
                                        className={`px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 w-full ${projectIsReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        placeholder={t('templates.projectTitlePlaceholder')}
                                      />
                                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                        {t('templates.projectTitle')}
                                      </div>
                                    </div>
                                    <div className="relative group">
                                      <input
                                        type="text"
                                        value={proj.description}
                                        onChange={(e) => updateProject(visionIndex, objectiveIndex, strategyIndex, projIndex, { ...proj, description: e.target.value }, nestedPath.length > 0 ? nestedPath : undefined)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        readOnly={projectIsReadOnly}
                                        disabled={projectIsReadOnly}
                                        className={`px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 w-full ${projectIsReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        placeholder={t('templates.projectDescriptionPlaceholder') || 'Project description'}
                                      />
                                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                        {t('templates.projectDescription')}
                                      </div>
                                    </div>
                                    <div className="relative group">
                                      <input
                                        type="number"
                                        value={proj.estimated_duration_months}
                                        onChange={(e) => updateProject(visionIndex, objectiveIndex, strategyIndex, projIndex, { ...proj, estimated_duration_months: parseInt(e.target.value) || 0 }, nestedPath.length > 0 ? nestedPath : undefined)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        readOnly={projectIsReadOnly}
                                        disabled={projectIsReadOnly}
                                        className={`px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 w-full ${projectIsReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        placeholder={t('templates.durationPlaceholder') || 'Duration (months)'}
                                        min="1"
                                        max="120"
                                      />
                                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                        Duration in Months (Max: 120)
                                      </div>
                                    </div>
                                    <div className="relative group">
                                      <input
                                        type="number"
                                        step="1"
                                        value={proj.estimated_cost}
                                        onChange={(e) => updateProject(visionIndex, objectiveIndex, strategyIndex, projIndex, { ...proj, estimated_cost: parseFloat(e.target.value) || 0 }, nestedPath.length > 0 ? nestedPath : undefined)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        readOnly={projectIsReadOnly}
                                        disabled={projectIsReadOnly}
                                        className={`px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500 w-full ${projectIsReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        placeholder={t('templates.budgetPlaceholder') || 'Budget ($K)'}
                                        min="0"
                                      />
                                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                        Budget in Thousands ($K)
                                      </div>
                                    </div>
                                  </div>

                                  {/* Project KPIs - Hidden in roadmap mode */}
                                  {!isRoadmapMode && (
                                  <div className="mt-2 ml-4">
                                    <div className="flex justify-between items-center mb-1">
                                      <h6 className="text-xs font-medium text-blue-700">📊 KPIs</h6>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          addKPI([visionIndex, objectiveIndex, strategyIndex, projIndex, ...nestedPath], 'PROJECT');
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="bg-gray-200 text-gray-700 p-1 rounded hover:bg-gray-300"
                                        title={t('templates.addKPI') || 'Add KPI'}
                                      >
                                        [+]
                                      </button>
                                    </div>
                                    {proj.kpis?.map((kpi, kpiIndex) => (
                                      <div key={kpiIndex} className="bg-blue-50 border border-blue-200 rounded p-1 mb-1">
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs text-blue-800">{kpi.name}</span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              confirmDelete(() => removeKPI([visionIndex, objectiveIndex, strategyIndex, projIndex, ...nestedPath], kpiIndex, 'PROJECT'), 'KPI');
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="bg-gray-200 text-red-600 p-1 rounded hover:bg-gray-300"
                                            title={t('templates.removeKPI') || 'Remove KPI'}
                                          >
                                            [-]
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  )}

                                  {/* Project Risks - Hidden in roadmap mode */}
                                  {!isRoadmapMode && (
                                  <div className="mt-2 ml-4">
                                    <div className="flex justify-between items-center mb-1">
                                      <h6 className="text-xs font-medium text-red-700">⚠️ Risks</h6>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          addRisk([visionIndex, objectiveIndex, strategyIndex, projIndex, ...nestedPath], 'PROJECT');
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="bg-gray-200 text-gray-700 p-1 rounded hover:bg-gray-300"
                                        title={t('templates.addRisk') || 'Add Risk'}
                                      >
                                        [+]
                                      </button>
                                    </div>
                                    {proj.risks?.map((risk, riskIndex) => (
                                      <div key={riskIndex} className="bg-red-50 border border-red-200 rounded p-1 mb-1">
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs text-red-800">{risk.name} ({risk.probability}/{risk.impact})</span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              confirmDelete(() => removeRisk([visionIndex, objectiveIndex, strategyIndex, projIndex, ...nestedPath], riskIndex, 'PROJECT'), 'Risk');
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="bg-gray-200 text-red-600 p-1 rounded hover:bg-gray-300"
                                            title={t('templates.removeRisk') || 'Remove Risk'}
                                          >
                                            [-]
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  )}

                                  {/* Project Stakeholders - Hidden in roadmap mode */}
                                  {!isRoadmapMode && (
                                  <div className="mt-2 ml-4">
                                    <div className="flex justify-between items-center mb-1">
                                      <h6 className="text-xs font-medium text-green-700">👥 Stakeholders</h6>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          addStakeholder([visionIndex, objectiveIndex, strategyIndex, projIndex, ...nestedPath], 'PROJECT');
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="bg-gray-200 text-gray-700 p-1 rounded hover:bg-gray-300"
                                        title={t('templates.addStakeholder') || 'Add Stakeholder'}
                                      >
                                        [+]
                                      </button>
                                    </div>
                                    {proj.stakeholders?.map((stakeholder, stakeholderIndex) => (
                                      <div key={stakeholderIndex} className="bg-green-50 border border-green-200 rounded p-1 mb-1">
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs text-green-800">{stakeholder.name} ({stakeholder.role})</span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              confirmDelete(() => removeStakeholder([visionIndex, objectiveIndex, strategyIndex, projIndex, ...nestedPath], stakeholderIndex, 'PROJECT'), 'Stakeholder');
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="bg-gray-200 text-red-600 p-1 rounded hover:bg-gray-300"
                                            title={t('templates.removeStakeholder') || 'Remove Stakeholder'}
                                          >
                                            [-]
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  )}

                                  {/* Inchstones - Hidden in roadmap mode */}
                                  {!isRoadmapMode && (
                                  <div className="mt-2 ml-4">
                                    <div className="flex justify-between items-center mb-1">
                                      <h6 className="text-xs font-medium text-red-700">⏰ Inchstones</h6>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          addInchstone(visionIndex, objectiveIndex, strategyIndex, projIndex, nestedPath.length > 0 ? nestedPath : undefined);
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="bg-gray-200 text-gray-700 p-1 rounded hover:bg-gray-300"
                                        title={t('templates.addInchstone') || 'Add Inchstone'}
                                      >
                                        [+]
                                      </button>
                                    </div>
                                    {proj.inchstones?.map((inchstone, inchstoneIndex) => (
                                      <div key={inchstoneIndex} className="border border-red-200 rounded-lg p-2 mb-1 bg-red-50 ml-4">
                                        <div className="flex justify-between items-center mb-1">
                                          <h6 className="text-xs font-medium text-red-900">⏰ Week {inchstone.week_number}: {inchstone.title}</h6>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              confirmDelete(() => {
                                                const updatedProject = {
                                                  ...proj,
                                                  inchstones: proj.inchstones?.filter((_, i) => i !== inchstoneIndex) || []
                                                };
                                                updateProject(visionIndex, objectiveIndex, strategyIndex, projIndex, updatedProject, nestedPath.length > 0 ? nestedPath : undefined);
                                              }, 'Inchstone');
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="bg-gray-200 text-red-600 p-1 rounded hover:bg-gray-300"
                                            title={t('templates.removeInchstone') || 'Remove Inchstone'}
                                          >
                                            [-]
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
                                          <div className="relative group">
                                            <input
                                              type="text"
                                              value={inchstone.title}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                const updatedInchstone = { ...inchstone, title: e.target.value };
                                                const updatedInchstones = proj.inchstones?.map((inch, i) => 
                                                  i === inchstoneIndex ? updatedInchstone : inch
                                                ) || [];
                                                const updatedProject = { ...proj, inchstones: updatedInchstones };
                                                updateProject(visionIndex, objectiveIndex, strategyIndex, projIndex, updatedProject, nestedPath.length > 0 ? nestedPath : undefined);
                                              }}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-red-500 w-full"
                                              placeholder={t('templates.inchstoneNamePlaceholder') || 'Inchstone name'}
                                            />
                                            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                              Inchstone Name
                                            </div>
                                          </div>
                                          <div className="relative group">
                                            <input
                                              type="number"
                                              value={inchstone.week_number}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                const updatedInchstone = { ...inchstone, week_number: parseInt(e.target.value) || 0 };
                                                const updatedInchstones = proj.inchstones?.map((inch, i) => 
                                                  i === inchstoneIndex ? updatedInchstone : inch
                                                ) || [];
                                                const updatedProject = { ...proj, inchstones: updatedInchstones };
                                                updateProject(visionIndex, objectiveIndex, strategyIndex, projIndex, updatedProject, nestedPath.length > 0 ? nestedPath : undefined);
                                              }}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-red-500 w-full"
                                              placeholder={t('templates.weekNumberPlaceholder') || 'Week number'}
                                              min="0"
                                            />
                                            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                              Week Number (0, 1, 2, ...)
                                            </div>
                                          </div>
                                <div className="relative group">
                                  <input
                                    type="file"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        // Read file as Base64 and store in template
                                        const reader = new FileReader();
                                        reader.onload = (event) => {
                                          const base64Content = event.target?.result as string;
                                          const updatedInchstone = { 
                                            ...inchstone, 
                                            attached_file: {
                                              filename: file.name,
                                              content: base64Content,
                                              mime_type: file.type,
                                              size: file.size
                                            }
                                          };
                                          const updatedInchstones = proj.inchstones?.map((inch, i) => 
                                            i === inchstoneIndex ? updatedInchstone : inch
                                          ) || [];
                                          const updatedProject = { ...proj, inchstones: updatedInchstones };
                                          updateProject(visionIndex, objectiveIndex, strategyIndex, projIndex, updatedProject, nestedPath.length > 0 ? nestedPath : undefined);
                                        };
                                        reader.readAsDataURL(file);
                                      }
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-red-500 w-full file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                                  />
                                  <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                    Attach File (PDF, DOC, XLS, PPT, Images)
                                  </div>
                                </div>
                                {inchstone.attached_file && (
                                  <div className="mt-1 text-xs">
                                    <button
                                      onClick={() => openFileViewer(inchstone.attached_file)}
                                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                      title={t('templates.clickToViewFile') || 'Click to view file'}
                                    >
                                      📎 {inchstone.attached_file.filename} ({(inchstone.attached_file.size / 1024).toFixed(1)} KB)
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                                  )}

                                  {/* Nested Projects - Recursive rendering */}
                                  {isProgram && proj.projects && Array.isArray(proj.projects) && proj.projects.length > 0 && (
                                    <div className="mt-2">
                                      <div className="text-xs font-medium text-orange-700 mb-1">Projects:</div>
                                      {proj.projects.map((nestedProj: PureProject, nestedIndex: number) => 
                                        renderProject(nestedProj, projIndex, [...nestedPath, nestedIndex], depth + 1)
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        };

                        return renderProject(project, projectIndex);
                      })}
                        </>
                      )}
                    </div>
                  );
                  })}
                    </>
                  )}
                </div>
                );
              })}
                </>
              )}
            </div>
            );
          })}

          {template.hierarchy.visions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>{t('templates.noVisionsAddedYet')}</p>
            </div>
          )}
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}

        {/* File Viewer Modal */}
        {viewingFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[var(--color-secondary-text)]">
                  📎 {viewingFile.name}
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => downloadFile(viewingFile)}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                  >
                    {t('templates.download')}
                  </button>
                  <button
                    onClick={closeFileViewer}
                    className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                  >
                    {t('templates.close')}
                  </button>
                </div>
              </div>
              
              <div className="border rounded-lg p-4 bg-gray-50">
                {viewingFile.mimeType.startsWith('image/') ? (
                  <img
                    src={`data:${viewingFile.mimeType};base64,${viewingFile.content}`}
                    alt={viewingFile.name}
                    className="max-w-full max-h-96 mx-auto"
                  />
                ) : viewingFile.mimeType.startsWith('text/') || 
                    viewingFile.mimeType === 'application/json' ||
                    viewingFile.mimeType === 'application/javascript' ||
                    viewingFile.mimeType === 'application/xml' ? (
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 max-h-96 overflow-auto">
                    {atob(viewingFile.content)}
                  </pre>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-4">
                      {t('templates.previewNotAvailable').replace('{mimeType}', viewingFile.mimeType)}
                    </p>
                    <button
                      onClick={() => downloadFile(viewingFile)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      {t('templates.downloadToView')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* JSON Editor Tab - Available for templates and roadmaps (read-only for existing roadmaps) */}
        {activeTab === 'json' && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[var(--color-secondary-text)]">
                {t('templates.jsonEditor')}
                {isRoadmapMode && editRoadmapId && (
                  <span className="ml-2 text-sm font-normal text-gray-500">(Read-only - Editing existing roadmap)</span>
                )}
              </h2>
              {/* Only show Apply button if not editing existing roadmap */}
              {!(isRoadmapMode && editRoadmapId) && (
                <button
                  onClick={applyJsonChanges}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  {t('templates.applyChanges')}
                </button>
              )}
            </div>
            <textarea
              value={jsonContent}
              onChange={(e) => handleJsonChange(e.target.value)}
              readOnly={isRoadmapMode && !!editRoadmapId}
              className={`w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm ${
                isRoadmapMode && editRoadmapId
                  ? 'bg-gray-100 cursor-not-allowed'
                  : 'focus:ring-2 focus:ring-purple-500 focus:border-transparent'
              }`}
              placeholder={t('templates.enterJsonPlaceholder')}
            />
            <p className="text-sm text-gray-600 mt-2">
              {isRoadmapMode && editRoadmapId
                ? 'JSON editing is read-only for existing roadmaps. Use the visual builder to make changes.'
                : t('templates.editJsonDescription')
              }
            </p>
          </div>
        )}
      </div>
      </div>
    </PageContextWrapper>
  );
};
